<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/plugins/axios';
import EvalTable from '@/components/table/Table.vue';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import TrajectoryViewer from '@/components/TrajectoryViewer.vue';
import Alert from '@/components/Alert.vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { fetchDatasets, fetchDatasetById, fetchTags } from '@/services/api';
import { fetchLLMs } from '@/pages/explore/services/exploreService';
import { fetchAnnotation, createAnnotation, updateAnnotation } from '@/services/annotationService';
import { useToast } from '@/composables/use-toast';
import { useRunWithModels } from '@/composables/useRunWithModels';
import { getFriendlyErrorMessage } from '@/utils/errorUtils.js';
import RunModelBenchmarkModal from '@/components/modals/RunModelBenchmarkModal.vue';

const { addToast } = useToast();
const { isOpening: isOpeningRunModal, openWithFeedback } = useRunWithModels();
const { SelectInput } = useLayoutComponents();
const route = useRoute();
const router = useRouter();
const props = defineProps({
  testId: {
    type: String,
    default: 'annotate-studio',
  },
});

const isRunModelModalOpen = ref(false);
const datasetRows = ref([]);
const datasetsById = ref(new Map());
const evalRunsByDataset = ref(new Map());
const leaderboardByDataset = ref(new Map());
const evalTotalsByDataset = ref(new Map());
const datasetDetails = ref(null);
const tasksTableRows = ref([]);
const taskScoresById = ref({});
const taskPassesById = ref({});
const taskThresholdsById = ref({});
const judgeExpandedByKey = ref({});
const rankedLlmIds = ref([null, null, null]);
const draggedLlmId = ref(null);
const reportByLlmId = ref(new Map());
const reportLoadingByLlmId = ref(new Map());
const reportResultsByLlmId = ref(new Map());
const modelChips = ref([]);
const llmsByName = ref(new Map());
const llmsById = ref(new Map());
const selectedLlmId = ref(null);
const modelResponse = ref('');
const modelThinking = ref('');
const agentTrajectory = ref(null);
const agentInfo = ref(null);
const agentRunInProgress = ref(false);
const harborEnvironment = ref({ task_toml: null, dockerfile: null });
let trajectoryEventSource = null;
const modelThinkingDisplay = computed(() => {
  const raw = modelThinking.value || '';
  return raw.trim().length > 0
    ? raw
    : 'No reasoning tokens used in this response, or this model does not use/expose reasoning.';
});
const notes = ref([]);
const selectedTags = ref([]);
const annotationByLlmId = ref({});
const isSaving = ref(false);
const lastSelectedLlmId = ref(null);
const showSaveAlert = ref(false);
const alertTitle = ref('Annotations Saved. Update eval?');
const alertMessage = ref('Would you like to update your eval based on these responses?');
const alertType = ref('success');
const datasetSortBy = ref('');
const datasetSortOrder = ref('asc');
const taskSortBy = ref('');
const taskSortOrder = ref('asc');
const datasetPage = ref(1);
const datasetTotalItems = ref(0);
const datasetLoadRequestId = ref(0);
const tasksPage = ref(1);
const tableItemsPerPage = 10;
const evalRunsTotalItems = ref(0);

const tags = ref([]);
const isLoading = ref({
  datasets: false,
  tasks: false,
  task: false,
  environment: false,
});

const datasetId = computed(() => route.query.datasetId || null);
const version = computed(() => Number(route.query.version || 1));
const taskId = computed(() => route.query.taskId || null);
const tasksTab = ref('summary');
const responseTab = ref('response');
const detailsTab = ref('notes');
const expandedTaskIds = ref([]);
const selectedTasksVersion = ref(null);
const annotateDetailBodyRef = ref(null);

const viewMode = computed(() => {
  if (!datasetId.value) return 'datasets';
  if (!taskId.value) return 'tasks';
  return 'task';
});

const isAgenticEval = computed(() => datasetDetails.value?.eval_type === 'agentic');

const getApiBaseUrl = () => {
  const configuredUrl = String(process.env.VUE_APP_BACKEND_URL || '').trim();
  if (configuredUrl) {
    return configuredUrl.replace(/\/+$/, '');
  }

  if (typeof window === 'undefined') {
    return '';
  }

  const { protocol, hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:3000`;
  }

  return origin;
};

const datasetVersionOptions = computed(() => {
  const available = datasetDetails.value?.versions?.available;
  if (Array.isArray(available) && available.length > 0) {
    return available.map((entry) => ({
      value: Number(entry.version),
      label: `v${entry.version}`,
    }));
  }
  const currentVersion = Number(version.value || 1);
  return [{ value: currentVersion, label: `v${currentVersion}` }];
});

const datasetColumns = [
  { key: 'view', label: '', type: 'text', sortable: false, align: 'center' },
  {
    key: 'dataset_name',
    label: ['EVAL', 'NAME'],
    type: 'text',
    sortable: true,
    align: 'left',
    wrap: true,
    truncate: 28,
    textClass: 'max-w-[180px]',
  },
  { key: 'latest_version', label: ['LATEST', 'VERSION'], type: 'text', sortable: true, align: 'left', wrap: true },
  { key: 'task_count', label: ['TASK', 'COUNT'], type: 'number', sortable: true, align: 'left', wrap: true },
  { key: 'avg_performance', label: ['AVG', 'PERFORMANCE'], type: 'text', sortable: true, align: 'left', wrap: true },
  {
    key: 'run_time',
    label: ['TOTAL', 'RUN TIME'],
    type: 'text',
    sortable: true,
    align: 'left',
    wrap: true,
    formatter: (value) => formatRunTime(value),
  },
  { key: 'model_count', label: ['MODEL', 'COUNT'], type: 'number', sortable: true, align: 'left', wrap: true },
  { key: 'top_model', label: ['TOP', 'MODEL'], type: 'text', sortable: true, align: 'left', wrap: true },
  { key: 'issue_date', label: ['CREATED', 'AT'], type: 'text', sortable: true, align: 'left', wrap: true },
];
const datasetRowRedirects = datasetColumns.map((column) => column.key);

const tasksColumns = [
  { key: 'view', label: '', type: 'text', sortable: false, align: 'center' },
  { key: 'task_id', label: 'TASK ID', type: 'text', sortable: true, align: 'left' },
  {
    key: 'task_preview',
    label: 'TASK PREVIEW',
    type: 'text',
    sortable: false,
    align: 'left',
    textClass: 'text-sm text-white max-w-[240px] truncate',
  },
  { key: 'criteria_count', label: 'CRITERIA', type: 'number', sortable: true, align: 'left' },
  { key: 'reference_files', label: 'REFERENCE FILES', type: 'number', sortable: true, align: 'left' },
  { key: 'pass_threshold', label: 'PASS THRESHOLD', type: 'text', sortable: true, align: 'left' },
  { key: 'avg_performance', label: 'AVG PERFORMANCE', type: 'text', sortable: true, align: 'left' },
  { key: 'passed', label: 'PASSED', type: 'text', sortable: true, align: 'left' },
];
const taskRowRedirects = tasksColumns.map((column) => column.key);

const selectedTask = computed(() => {
  return datasetDetails.value?.tasks?.find((task) => task.task_id === taskId.value) || null;
});

const selectedAnswer = computed(() => {
  return datasetDetails.value?.answers?.find((answer) => answer.task_id === taskId.value) || null;
});

const goldenAnswer = computed(() => selectedAnswer.value?.answer || '—');
const taskRubric = computed(() => {
  if (selectedAnswer.value?.criteria?.length) return normalizeCriteria(selectedAnswer.value.criteria);
  if (selectedTaskResult.value?.rubric?.criteria?.length) {
    return normalizeCriteria(selectedTaskResult.value.rubric.criteria);
  }
  if (selectedTaskResult.value?.per_criterion?.length) {
    const criteria = selectedTaskResult.value.per_criterion.map((entry) => entry?.criterion).filter(Boolean);
    return normalizeCriteria(criteria);
  }
  return [];
});

const referenceFiles = computed(() => {
  const entry = selectedTask.value?.reference_file;
  if (!entry) return [];
  if (Array.isArray(entry)) {
    return entry.filter(Boolean);
  }
  return [entry];
});

const getReferenceFileName = (item) => {
  if (!item) return '';
  if (typeof item === 'string') {
    const parts = item.split('/');
    return parts[parts.length - 1];
  }
  return item.filename || item.name || '';
};

const isImageFile = (name) => {
  if (!name) return false;
  const lower = name.toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].some((ext) => lower.endsWith(ext));
};

const getReferenceFileUrl = (item) => {
  const baseUrl = getApiBaseUrl();
  if (!item) return '';
  if (typeof item === 'string' && item.startsWith('http')) return item;
  const sellerId = datasetDetails.value?.seller_id || datasetDetails.value?.seller?.id;
  const evalId = datasetDetails.value?.eval_id;
  const filename = getReferenceFileName(item);
  if (!filename || !sellerId || !evalId) return '';
  const path = filename.startsWith('/')
    ? filename
    : `/${sellerId}/${evalId}/v${version.value}/refs/${filename}`;
  return `${baseUrl}/api/files/download?path=${encodeURIComponent(path)}`;
};

const selectedTaskResult = computed(() => {
  if (!selectedLlmId.value || !taskId.value) return null;
  return getTaskResultForModel(selectedLlmId.value, taskId.value);
});

const toTaskIdString = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

const getResultThresholdPercent = (result) => {
  const thresholdValue =
    result?.pass_threshold ??
    result?.passThreshold ??
    result?.threshold ??
    result?.pass_threshold_pct ??
    null;
  if (thresholdValue == null) return null;
  const numericThreshold = Number(thresholdValue);
  if (!Number.isFinite(numericThreshold)) return null;
  if (numericThreshold > 0 && numericThreshold <= 1) {
    return numericThreshold * 100;
  }
  return numericThreshold;
};

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeRatioToPercent = (value) => {
  if (value == null) return null;
  return value > 0 && value <= 1 ? value * 100 : value;
};

const getPerCriterionScorePercent = (result) => {
  if (!Array.isArray(result?.per_criterion)) return null;
  const scores = result.per_criterion
    .map((criterion) => toFiniteNumber(criterion?.final_score))
    .filter((score) => score != null)
    .map((score) => normalizeRatioToPercent(score));
  if (scores.length === 0) return null;
  const total = scores.reduce((sum, score) => sum + score, 0);
  return total / scores.length;
};

const getCriteriaProgress = (result) => {
  const totalCriteria = toFiniteNumber(result?.total_criteria ?? 0);
  const passedCount = toFiniteNumber(result?.passed_count ?? 0);
  if (!(totalCriteria > 0 && passedCount != null)) return null;
  return {
    totalCriteria,
    passedCount,
    criteriaPct: (passedCount / totalCriteria) * 100,
  };
};

const normalizeKnownVerdict = (value) => {
  const verdict = String(value || '').toLowerCase();
  if (verdict === 'pass' || verdict === 'fail' || verdict === 'flagged') {
    return verdict;
  }
  return null;
};

const verdictFromPassedBoolean = (value) => {
  if (typeof value !== 'boolean') return null;
  return value ? 'pass' : 'fail';
};

const verdictFromScoreThreshold = (result) => {
  const scorePercent = getResultScorePercent(result);
  const thresholdPercent = getResultThresholdPercent(result);
  if (scorePercent == null || thresholdPercent == null) return null;
  return scorePercent >= thresholdPercent ? 'pass' : 'fail';
};

const verdictFromCriteriaProgress = (result) => {
  const progress = getCriteriaProgress(result);
  if (!progress) return null;
  const thresholdPercent = getResultThresholdPercent(result);
  if (thresholdPercent != null) {
    return progress.criteriaPct >= thresholdPercent ? 'pass' : 'fail';
  }
  return progress.passedCount === progress.totalCriteria ? 'pass' : 'fail';
};

const getResultScorePercent = (result) => {
  const rawScorePercent = toFiniteNumber(
    result?.score_percent ?? result?.scorePercent ?? result?.total_score_raw ?? null
  );
  if (rawScorePercent != null) return normalizeRatioToPercent(rawScorePercent);

  const rawScore = toFiniteNumber(result?.score ?? result?.total_score ?? null);
  if (rawScore != null) return normalizeRatioToPercent(rawScore);

  const criterionScorePercent = getPerCriterionScorePercent(result);
  if (criterionScorePercent != null) return criterionScorePercent;

  const progress = getCriteriaProgress(result);
  if (!progress) return null;
  return progress.criteriaPct;
};

const getResultVerdict = (result) => {
  if (!result) return null;
  const directVerdict = normalizeKnownVerdict(result?.verdict);
  if (directVerdict) return directVerdict;
  if (result?.flagged_for_human === true) return 'flagged';

  const booleanVerdict = verdictFromPassedBoolean(result?.passed ?? result?.portex_passed ?? null);
  if (booleanVerdict) return booleanVerdict;

  const thresholdVerdict = verdictFromScoreThreshold(result);
  if (thresholdVerdict) return thresholdVerdict;

  return verdictFromCriteriaProgress(result);
};

const resolveTaskIdForResult = (result) => {
  if (!result || typeof result !== 'object') return null;

  const directTaskId = toTaskIdString(result.task_id) || toTaskIdString(result.taskId);
  if (directTaskId) return directTaskId;

  const taskList = Array.isArray(datasetDetails.value?.tasks) ? datasetDetails.value.tasks : [];
  if (taskList.length === 1) {
    return toTaskIdString(taskList[0]?.task_id);
  }

  const hasZeroBasedIndex = result.task_idx != null || result.taskIndex != null;
  const indexValue = Number(
    result.task_idx ??
    result.taskIndex ??
    result.task_number ??
    result.taskNumber
  );
  if (Number.isInteger(indexValue) && taskList.length > 0) {
    const taskIndex = hasZeroBasedIndex ? indexValue : indexValue - 1;
    if (taskIndex >= 0 && taskIndex < taskList.length) {
      return toTaskIdString(taskList[taskIndex]?.task_id);
    }
  }

  const byTaskName = toTaskIdString(result.task_name) || toTaskIdString(result.taskName);
  if (byTaskName) {
    const hasMatchingTaskId = taskList.some((task) => String(task?.task_id || '') === byTaskName);
    if (hasMatchingTaskId) {
      return byTaskName;
    }
  }

  return null;
};

const buildResultMap = (results) => {
  const resultMap = new Map();
  if (!Array.isArray(results)) return resultMap;
  results.forEach((result) => {
    const resolvedTaskId = resolveTaskIdForResult(result);
    if (!resolvedTaskId) return;
    resultMap.set(resolvedTaskId, {
      ...result,
      task_id: resolvedTaskId,
    });
  });
  return resultMap;
};

const selectedTaskVerdict = computed(() => {
  const verdict = getResultVerdict(selectedTaskResult.value);
  if (verdict === 'pass') return 'pass';
  if (verdict === 'fail') return 'fail';
  if (verdict === 'flagged') return 'flagged';
  return null;
});

const normalizeTextKey = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeCriterionKey = (value) => normalizeTextKey(value);
const normalizeTagName = (value) => normalizeTextKey(value);

const normalizeTagIds = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (item && typeof item === 'object') {
        return Number(item.id ?? item.tag_id ?? item.value);
      }
      return Number(item);
    })
    .filter((id) => Number.isFinite(id));
};

const rankTagIds = computed(() => {
  const map = { 1: null, 2: null, 3: null };
  tags.value.forEach((tag) => {
    const name = normalizeTagName(tag?.name);
    if (name === 'rank 1') map[1] = Number(tag.id);
    if (name === 'rank 2') map[2] = Number(tag.id);
    if (name === 'rank 3') map[3] = Number(tag.id);
  });
  return map;
});

const rankTagsReady = computed(() => {
  return Boolean(rankTagIds.value[1] && rankTagIds.value[2] && rankTagIds.value[3]);
});

const displayTags = computed(() => {
  const rankIds = [rankTagIds.value[1], rankTagIds.value[2], rankTagIds.value[3]].filter(Boolean);
  return tags.value.filter((tag) => !rankIds.includes(Number(tag.id)));
});

const formatCriterionTestId = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const getCriterionEntryFromResult = (result, criterion) => {
  if (!result?.per_criterion) return null;
  const entries = Array.isArray(result.per_criterion) ? result.per_criterion : [];
  const candidateTexts = [];
  if (typeof criterion === 'string') {
    candidateTexts.push(normalizeCriterionKey(criterion));
  } else {
    [criterion?.name, criterion?.semanticPrompt, criterion?.description]
      .filter(Boolean)
      .forEach((value) => candidateTexts.push(normalizeCriterionKey(value)));
  }
  return (
    entries.find((entry) => candidateTexts.includes(normalizeCriterionKey(entry?.criterion))) || null
  );
};

const getCriterionVerdict = (criterion) => {
  const match = getCriterionEntryFromResult(selectedTaskResult.value, criterion);
  if (!match) return null;
  const finalScore = match.final_score;
  if (finalScore === 'contested') {
    return 'flagged';
  }
  if (finalScore == null) return null;
  const numericScore = Number(finalScore);
  if (!Number.isFinite(numericScore)) return null;
  return numericScore === 0 ? 'fail' : 'pass';
};

const normalizeCriteria = (criteria) => {
  if (!Array.isArray(criteria)) return [];
  return criteria
    .map((criterion, index) => {
      if (criterion && typeof criterion === 'object') {
        return {
          id: criterion.id || normalizeCriterionKey(criterion.name || `criterion-${index}`),
          name: criterion.name || String(criterion.description || `Criterion ${index + 1}`),
          weight: criterion.weight,
          type: criterion.type,
          semanticPrompt: criterion.semanticPrompt,
          description: criterion.description,
        };
      }
      const name = String(criterion || '').trim();
      if (!name) return null;
      return {
        id: normalizeCriterionKey(name),
        name,
        weight: null,
        type: null,
        semanticPrompt: null,
        description: null,
      };
    })
    .filter(Boolean);
};

const getCriteriaForTask = (taskIdValue) => {
  const answers = Array.isArray(datasetDetails.value?.answers) ? datasetDetails.value.answers : [];
  const match = answers.find((answer) => answer.task_id === taskIdValue);
  if (Array.isArray(match?.criteria) && match.criteria.length) {
    return normalizeCriteria(match.criteria);
  }
  const reports = Array.from(reportResultsByLlmId.value.values());
  const reportMatch = reports
    .map((resultMap) => resultMap?.get(String(taskIdValue)))
    .find(Boolean);
  if (reportMatch?.rubric?.criteria?.length) {
    return normalizeCriteria(reportMatch.rubric.criteria);
  }
  if (Array.isArray(reportMatch?.per_criterion)) {
    const criteria = reportMatch.per_criterion.map((entry) => entry?.criterion).filter(Boolean);
    return normalizeCriteria(criteria);
  }
  return [];
};

const getCriterionVerdictForModel = (taskIdValue, llmId, criterion) => {
  const match = getCriterionEntryFromResult(getTaskResultForModel(llmId, taskIdValue), criterion);
  if (!match) return null;
  const finalScore = match.final_score;
  if (finalScore === 'contested') {
    return 'flagged';
  }
  if (finalScore == null) return null;
  const numericScore = Number(finalScore);
  if (!Number.isFinite(numericScore)) return null;
  return numericScore === 0 ? 'fail' : 'pass';
};

const rankedChipsByIndex = computed(() => {
  return rankedLlmIds.value.map((llmId) => modelChips.value.find((chip) => chip.id === llmId) || null);
});

const unrankedModelChips = computed(() => {
  const rankedIds = rankedLlmIds.value.filter(Boolean);
  return modelChips.value.filter((chip) => !rankedIds.includes(chip.id));
});

const setRankForLlm = (llmId, rankIndex) => {
  if (!llmId || rankIndex < 0 || rankIndex > 2) return;
  const next = [...rankedLlmIds.value];
  const existingIndex = next.findIndex((id) => id === llmId);
  if (existingIndex === rankIndex) return;
  const targetId = next[rankIndex] || null;
  if (existingIndex >= 0) {
    next[existingIndex] = targetId;
  }
  next[rankIndex] = llmId;
  rankedLlmIds.value = next;
};

const clearRankIndex = (rankIndex) => {
  if (rankIndex < 0 || rankIndex > 2) return;
  const next = [...rankedLlmIds.value];
  next[rankIndex] = null;
  rankedLlmIds.value = next;
};

const handleDragStart = (llmId) => {
  draggedLlmId.value = llmId;
};

const handleDropRank = (rankIndex) => {
  if (!draggedLlmId.value) return;
  setRankForLlm(draggedLlmId.value, rankIndex);
  draggedLlmId.value = null;
};

const handleDropUnranked = () => {
  if (!draggedLlmId.value) return;
  const next = rankedLlmIds.value.map((id) => (id === draggedLlmId.value ? null : id));
  rankedLlmIds.value = next;
  draggedLlmId.value = null;
};

const syncCurrentAnnotationDraft = (llmId = selectedLlmId.value) => {
  if (!llmId) return;
  const current = annotationByLlmId.value[llmId] || {};
  annotationByLlmId.value[llmId] = {
    ...current,
    notes_array: Array.isArray(notes.value) ? notes.value : [],
    tags: normalizeTagIds(selectedTags.value),
  };
};

const getRankTagIdsArray = () => {
  return [rankTagIds.value[1], rankTagIds.value[2], rankTagIds.value[3]].filter(Boolean);
};

const buildTagsWithRank = (tagsList, rankIndex) => {
  const rankIds = getRankTagIdsArray();
  const cleaned = normalizeTagIds(tagsList).filter((id) => !rankIds.includes(id));
  const rankTagId = rankTagIds.value[rankIndex + 1];
  if (rankTagId) {
    return [...cleaned, Number(rankTagId)];
  }
  return cleaned;
};

const syncRankingFromAnnotations = () => {
  if (!rankTagsReady.value) return;
  const next = [null, null, null];
  Object.entries(annotationByLlmId.value).forEach(([llmKey, annotation]) => {
    const llmIdNum = Number(llmKey);
    const tagIds = (annotation?.tags || []).map((id) => Number(id));
    if (rankTagIds.value[1] && tagIds.includes(rankTagIds.value[1]) && !next[0]) {
      next[0] = llmIdNum;
    }
    if (rankTagIds.value[2] && tagIds.includes(rankTagIds.value[2]) && !next[1]) {
      next[1] = llmIdNum;
    }
    if (rankTagIds.value[3] && tagIds.includes(rankTagIds.value[3]) && !next[2]) {
      next[2] = llmIdNum;
    }
  });
  rankedLlmIds.value = next;
};

const loadAllAnnotations = async () => {
  if (!datasetId.value || !taskId.value) return;
  const llmIds = modelChips.value.map((chip) => Number(chip.id)).filter(Boolean);
  await Promise.all(
    llmIds.map(async (llmId) => {
      if (annotationByLlmId.value[llmId]) return;
      try {
        const annotation = await fetchAnnotation(
          datasetId.value,
          version.value,
          taskId.value,
          llmId
        );
        annotationByLlmId.value[llmId] = annotation;
      } catch (error) {
        // Ignore missing annotations
      }
    })
  );
};

const persistRankingTags = async () => {
  if (!rankTagsReady.value || !datasetId.value || !taskId.value) return;
  const llmIds = modelChips.value.map((chip) => Number(chip.id)).filter(Boolean);
  for (const llmId of llmIds) {
    const rankIndex = rankedLlmIds.value.findIndex((id) => id === llmId);
    const annotation = annotationByLlmId.value[llmId];
    const nextTags = buildTagsWithRank(annotation?.tags || [], rankIndex);
    const currentTags = (annotation?.tags || []).map((id) => Number(id));
    const hasChanges =
      nextTags.length !== currentTags.length ||
      nextTags.some((id) => !currentTags.includes(id));
    if (!hasChanges) continue;
    const payload = {
      dataset_id: Number(datasetId.value),
      version: Number(version.value),
      task_id: String(taskId.value),
      llm_id: Number(llmId),
      notes_array: annotation?.notes_array || [],
      tags: nextTags,
    };
    if (annotation?.id) {
      await updateAnnotation(annotation.id, payload);
      annotationByLlmId.value[llmId] = { ...annotation, tags: nextTags };
    } else if (rankIndex >= 0) {
      const created = await createAnnotation(payload);
      annotationByLlmId.value[llmId] = created;
    }
  }
  if (selectedLlmId.value) {
    const selectedRankIndex = rankedLlmIds.value.findIndex((id) => id === selectedLlmId.value);
    selectedTags.value = buildTagsWithRank(selectedTags.value, selectedRankIndex);
  }
};

const saveAllAnnotations = async () => {
  if (!datasetId.value || !taskId.value) return;
  const savedLlmIds = [];
  const llmIds = modelChips.value.map((chip) => Number(chip.id)).filter(Boolean);
  for (const llmId of llmIds) {
    const annotation = annotationByLlmId.value[llmId];
    const rankIndex = rankedLlmIds.value.findIndex((id) => id === llmId);
    const baseTags = normalizeTagIds(annotation?.tags || []);
    const tagsWithRank = buildTagsWithRank(baseTags, rankIndex);
    const notesArray = Array.isArray(annotation?.notes_array) ? annotation.notes_array : [];
    const hasContent = tagsWithRank.length > 0 || notesArray.length > 0;
    if (!annotation?.id && !hasContent) continue;
    const payload = {
      dataset_id: Number(datasetId.value),
      version: Number(version.value),
      task_id: String(taskId.value),
      llm_id: Number(llmId),
      notes_array: notesArray,
      tags: tagsWithRank,
    };
    if (annotation?.id) {
      await updateAnnotation(annotation.id, payload);
      annotationByLlmId.value[llmId] = { ...annotation, tags: tagsWithRank, notes_array: notesArray };
      savedLlmIds.push(llmId);
    } else {
      const created = await createAnnotation(payload);
      annotationByLlmId.value[llmId] = created;
      savedLlmIds.push(llmId);
    }
  }
  if (selectedLlmId.value) {
    const selectedRankIndex = rankedLlmIds.value.findIndex((id) => id === selectedLlmId.value);
    selectedTags.value = buildTagsWithRank(selectedTags.value, selectedRankIndex);
  }
  return savedLlmIds;
};

const getJudgeVerdict = (passCount, failCount, totalVotes) => {
  if (!totalVotes) return '—';
  if (passCount > failCount) return 'pass';
  if (failCount > passCount) return 'fail';
  return 'flagged';
};

const getCriterionJudgeEntries = (criterion) => {
  const match = getCriterionEntryFromResult(selectedTaskResult.value, criterion);
  if (!match) return [];

  if (Array.isArray(match?.judge_votes)) {
    return match.judge_votes
      .map((judge) => {
        const voteValue = judge?.vote;
        let voteNumber = Number(voteValue);
        if (typeof voteValue === 'boolean') {
          voteNumber = voteValue ? 1 : 0;
        }
        const isPass = Number.isFinite(voteNumber) && voteNumber > 0;
        const passCount = isPass ? 1 : 0;
        const failCount = isPass ? 0 : 1;
        const totalVotes = 1;
        const justifications = judge?.justification != null ? [String(judge.justification)] : [];
        return {
          model: judge?.judge_name || judge?.model || 'judge',
          passCount,
          failCount,
          totalVotes,
          verdict: getJudgeVerdict(passCount, failCount, totalVotes),
          justifications,
        };
      })
      .filter((judge) => judge.totalVotes > 0 || judge.justifications.length > 0);
  }

  const intraJudge = match?.intra_judge;
  if (!intraJudge || typeof intraJudge !== 'object') return [];
  return Object.entries(intraJudge)
    .map(([model, data]) => {
      const votes = Array.isArray(data?.votes) ? data.votes : [];
      const rawJustifications = data?.justification;
      let justifications = [];
      if (Array.isArray(rawJustifications)) {
        justifications = rawJustifications.filter(Boolean).map((item) => String(item));
      } else if (rawJustifications != null) {
        justifications = [String(rawJustifications)];
      }
      const passCount = votes.filter((vote) => Number(vote) > 0).length;
      const failCount = votes.filter((vote) => Number(vote) <= 0).length;
      const totalVotes = votes.length;
      return {
        model,
        passCount,
        failCount,
        totalVotes,
        verdict: getJudgeVerdict(passCount, failCount, totalVotes),
        justifications,
      };
    })
    .filter((judge) => judge.totalVotes > 0 || judge.justifications.length > 0);
};

const getCriterionKey = (criterion) => {
  if (typeof criterion === 'string') return normalizeCriterionKey(criterion);
  return criterion?.id || normalizeCriterionKey(criterion?.name || 'criterion');
};

const isJudgeExpanded = (criterion) => {
  const key = getCriterionKey(criterion);
  return Boolean(judgeExpandedByKey.value[key]);
};

const toggleJudgeExpanded = (criterion) => {
  const key = getCriterionKey(criterion);
  judgeExpandedByKey.value = {
    ...judgeExpandedByKey.value,
    [key]: !judgeExpandedByKey.value[key],
  };
};

const isTaskExpanded = (taskIdValue) => {
  return expandedTaskIds.value.includes(taskIdValue);
};

const toggleTaskExpanded = (taskIdValue) => {
  if (isTaskExpanded(taskIdValue)) {
    expandedTaskIds.value = expandedTaskIds.value.filter((id) => id !== taskIdValue);
  } else {
    expandedTaskIds.value = [...expandedTaskIds.value, taskIdValue];
  }
};

const bestResponseTagId = computed(() => {
  const match = tags.value.find((tag) => String(tag.name || '').toLowerCase() === 'best response');
  return match ? Number(match.id) : null;
});

const normalizeName = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
};

const getLlmIcon = (llmId, modelName) => {
  if (llmId && llmsById.value.has(Number(llmId))) {
    return llmsById.value.get(Number(llmId))?.icon || null;
  }
  if (modelName) {
    return llmsByName.value.get(normalizeName(modelName))?.icon || null;
  }
  return null;
};

const getTaskResultForModel = (llmId, taskIdValue) => {
  const map = reportResultsByLlmId.value.get(Number(llmId));
  if (!map) return null;
  return map.get(taskIdValue) || null;
};

const reportCacheKey = (datasetIdVal, versionVal, llmIdVal) =>
  `${datasetIdVal}_${versionVal}_${llmIdVal}`;

const ensureReportForLlm = async (llmId) => {
  if (!datasetId.value || !llmId) return;
  const numericId = Number(llmId);
  const cacheKey = reportCacheKey(datasetId.value, version.value, numericId);
  if (reportByLlmId.value.has(cacheKey)) {
    const cached = reportByLlmId.value.get(cacheKey);
    const results = cached?.results || [];
    const resultMap = buildResultMap(results);
    reportResultsByLlmId.value.set(numericId, resultMap);
    return;
  }
  if (reportLoadingByLlmId.value.has(cacheKey)) {
    await reportLoadingByLlmId.value.get(cacheKey);
    return;
  }
  const request = axios
    .get(`/api/leaderboards/${datasetId.value}_v${version.value}/report`, {
      params: { llm_id: numericId },
    })
    .then((response) => {
      const report = response?.data?.report || null;
      reportByLlmId.value.set(cacheKey, report);
      const results = report?.results || [];
      const resultMap = buildResultMap(results);
      reportResultsByLlmId.value.set(numericId, resultMap);
      reportLoadingByLlmId.value.delete(cacheKey);
      return { llmId: numericId, report };
    })
    .catch((error) => {
      reportLoadingByLlmId.value.delete(cacheKey);
      throw error;
    });
  reportLoadingByLlmId.value.set(cacheKey, request);
  await request;
};

const getPassFailForTask = (llmId, taskIdValue) => {
  const result = getTaskResultForModel(llmId, taskIdValue);
  if (!result) return null;
  const verdict = getResultVerdict(result);
  if (verdict === 'pass') return 'pass';
  if (verdict === 'fail') return 'fail';
  return null;
};

const taskGridColumns = computed(() => {
  const leaderboard = leaderboardByDataset.value.get(Number(datasetId.value)) || [];
  return leaderboard
    .map((entry) => {
      const llmId =
        entry?.llm_id ??
        entry?.llmId ??
        entry?.llm?.id ??
        null;
      if (!llmId) return null;
      return {
        id: Number(llmId),
        name: entry.model_name || '—',
        icon: getLlmIcon(llmId, entry.model_name),
      };
    })
    .filter(Boolean);
});

const handleDatasetSort = (column) => {
  if (datasetSortBy.value === column) {
    datasetSortOrder.value = datasetSortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    datasetSortBy.value = column;
    datasetSortOrder.value = 'asc';
  }
};

const handleTaskSort = (column) => {
  if (taskSortBy.value === column) {
    taskSortOrder.value = taskSortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    taskSortBy.value = column;
    taskSortOrder.value = 'asc';
  }
};

const toNumber = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (raw.includes('/')) {
    const [left, right] = raw.split('/');
    const numerator = Number(left);
    const denominator = Number(right);
    if (Number.isFinite(numerator) && Number.isFinite(denominator) && denominator !== 0) {
      return numerator / denominator;
    }
  }
  const trimmed = raw.replace('%', '').trim();
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const compareValues = (aValue, bValue, order) => {
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return order === 'asc' ? 1 : -1;
  if (bValue == null) return order === 'asc' ? -1 : 1;
  if (aValue > bValue) return order === 'asc' ? 1 : -1;
  if (aValue < bValue) return order === 'asc' ? -1 : 1;
  return 0;
};

const sortedDatasetRows = computed(() => {
  if (!datasetSortBy.value) return datasetRows.value;
  return [...datasetRows.value].sort((a, b) => {
    const key = datasetSortBy.value;
    if (key === 'issue_date') {
      return compareValues(new Date(a[key]).getTime(), new Date(b[key]).getTime(), datasetSortOrder.value);
    }
    const numberKeys = ['task_count', 'model_count', 'avg_performance', 'run_time'];
    if (numberKeys.includes(key)) {
      return compareValues(toNumber(a[key]), toNumber(b[key]), datasetSortOrder.value);
    }
    return compareValues(String(a[key] ?? '').toLowerCase(), String(b[key] ?? '').toLowerCase(), datasetSortOrder.value);
  });
});

const sortedTasksTableRows = computed(() => {
  if (!taskSortBy.value) return tasksTableRows.value;
  return [...tasksTableRows.value].sort((a, b) => {
    const key = taskSortBy.value;
    const numberKeys = ['criteria_count', 'avg_performance', 'passed'];
    if (numberKeys.includes(key)) {
      return compareValues(toNumber(a[key]), toNumber(b[key]), taskSortOrder.value);
    }
    return compareValues(String(a[key] ?? '').toLowerCase(), String(b[key] ?? '').toLowerCase(), taskSortOrder.value);
  });
});

const truncateText = (text, maxLength = 120) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const LATENCY_UNIT_MS = { h: 3600000, m: 60000, s: 1000, ms: 1 };

function parseLatencyTokens(normalized) {
  const tokens = [];
  let i = 0;
  while (i < normalized.length) {
    const rest = normalized.slice(i);
    const match = rest.match(/^(\d+\.\d+)(ms|s|m|h)/) || rest.match(/^(\d+)(ms|s|m|h)/);
    if (match) {
      tokens.push({ amount: match[1], unit: match[2] });
      i += match[0].length;
    } else {
      i += 1;
    }
  }
  return tokens;
}

function tokensToMs(tokens) {
  let totalMs = 0;
  for (const { amount, unit } of tokens) {
    const num = Number(amount);
    if (!Number.isFinite(num)) continue;
    const multiplier = LATENCY_UNIT_MS[unit];
    if (multiplier != null) totalMs += num * multiplier;
  }
  return Number.isFinite(totalMs) ? totalMs : null;
}

function parseLatencyToMs(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.toLowerCase();
  const tokens = parseLatencyTokens(normalized);
  if (tokens.length > 0) {
    const totalMs = tokensToMs(tokens);
    if (totalMs != null) return totalMs;
  }

  const parsed = Number(normalized.replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatRunTime(ms) {
  if (ms == null) return '—';
  let value = ms;
  if (value === 'NA') {
    value = 0;
  }
  const numericMs = Number(value);
  if (!Number.isFinite(numericMs)) return '—';
  const totalSeconds = Math.floor(numericMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (totalSeconds < 1) {
    const decimalSeconds = numericMs / 1000;
    return `${decimalSeconds.toFixed(2)}s`;
  }
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
}

const getLatencyFromMetrics = (metrics) => {
  if (!metrics) return null;
  if (Array.isArray(metrics)) {
    return metrics[0]?.latency ?? null;
  }
  return metrics?.latency ?? null;
};

const normalizeModelText = (value) => {
  if (!value) return '';
  let text = String(value);
  // Normalize escaped newlines/tabs if they arrive as literal sequences
  text = text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  // Fix double-escaped LaTeX delimiters
  text = text
    .replace(/\\\\\(/g, '\\(')
    .replace(/\\\\\)/g, '\\)')
    .replace(/\\\\\[/g, '\\[')
    .replace(/\\\\\]/g, '\\]');
  return text;
};

const getDatasetRows = () => {
  const rows = [];
  datasetsById.value.forEach((dataset, id) => {
    const runsByVersion = evalRunsByDataset.value.get(id) || new Map();
    const leaderboard = leaderboardByDataset.value.get(id) || [];
    const evalTotalTasks = evalTotalsByDataset.value.get(id);
    const latestVersion =
      Number(dataset?.latest_version) ||
      Number(dataset?.versions?.current) ||
      1;
    const avgScore =
      leaderboard.length > 0
        ? (leaderboard.reduce((sum, item) => sum + Number(item.score || 0), 0) / leaderboard.length)
        : null;
    const topEntry = leaderboard[0] || null;
    const topModel = topEntry?.model_name || '—';
    const topModelId = topEntry?.llm_id ?? topEntry?.llmId ?? topEntry?.llm?.id ?? null;
    const totalRunTimeMs = (() => {
      const values = leaderboard
        .map((entry) => parseLatencyToMs(getLatencyFromMetrics(entry?.metrics)))
        .filter((latency) => Number.isFinite(latency));
      if (values.length === 0) return null;
      return values.reduce((sum, latency) => sum + latency, 0);
    })();

    if (runsByVersion.size === 0) {
      const versionNumber = latestVersion;
      rows.push({
        dataset_id: id,
        view: 'view',
        dataset_name: dataset.name || '—',
        latest_version: `v${latestVersion}`,
        task_count: evalTotalTasks ?? dataset.total_tasks ?? dataset?.tasks?.length ?? '—',
        avg_performance: avgScore != null ? `${Math.round(avgScore * 100)}%` : '—',
        run_time: totalRunTimeMs,
        model_count: leaderboard.length || '—',
        top_model: topModel,
        top_model_id: topModelId,
        issue_date: formatDate(dataset?.created_at),
        eval_run_id: null,
        version_number: versionNumber,
      });
      return;
    }

    const versionNumber = latestVersion || Math.max(...Array.from(runsByVersion.keys()).map(Number));
    const runs = runsByVersion.get(versionNumber) || [];
    const latestRun = [...runs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    rows.push({
      dataset_id: id,
      view: 'view',
      dataset_name: dataset.name || '—',
      latest_version: `v${latestVersion}`,
      task_count: evalTotalTasks ?? dataset.total_tasks ?? dataset?.tasks?.length ?? '—',
      avg_performance: avgScore != null ? `${Math.round(avgScore * 100)}%` : '—',
      run_time: totalRunTimeMs,
      model_count: leaderboard.length || '—',
      top_model: topModel,
      top_model_id: topModelId,
      issue_date: formatDate(dataset?.created_at),
      eval_run_id: latestRun?.id || null,
      version_number: versionNumber,
    });
  });
  datasetRows.value = rows;
};

const buildEvalRunsIndex = (runs) => {
  const datasetMap = new Map();
  runs.forEach((run) => {
    const datasetIdFromRun =
      run?.sale?.listing?.datasets?.[0]?.id ||
      run?.sale?.listing?.datasets?.[0]?.dataset_id ||
      run?.sale?.listing?.datasets?.[0]?.dataset?.id;
    if (!datasetIdFromRun) return;

    const versionNumber = run?.version || 1;
    if (!datasetMap.has(datasetIdFromRun)) {
      datasetMap.set(datasetIdFromRun, new Map());
    }
    const versionMap = datasetMap.get(datasetIdFromRun);
    if (!versionMap.has(versionNumber)) {
      versionMap.set(versionNumber, []);
    }
    versionMap.get(versionNumber).push(run);
  });
  evalRunsByDataset.value = datasetMap;
};

const fetchLeaderboard = async (id) => {
  try {
    const response = await axios.get(`/api/eval-runs/leaderboard/${id}`);
    const leaderboards = response.data?.leaderboards || [];
    leaderboardByDataset.value.set(id, leaderboards);
  } catch (error) {
    leaderboardByDataset.value.set(id, []);
  }
};

const ensureLeaderboard = async (id) => {
  if (!leaderboardByDataset.value.has(id)) {
    await fetchLeaderboard(id);
  }
};

const loadDatasetRows = async () => {
  const requestId = ++datasetLoadRequestId.value;
  const currentPage = datasetPage.value;
  isLoading.value.datasets = true;
  try {
    const skip = (currentPage - 1) * tableItemsPerPage;
    const take = tableItemsPerPage;
    const evalDatasets = await fetchDatasets(skip, take, 'eval', true);
    const normalizedEvalDatasets = Array.isArray(evalDatasets) ? evalDatasets : [];
    if (requestId !== datasetLoadRequestId.value) return;
    const datasetMap = new Map();
    normalizedEvalDatasets.forEach((dataset) => {
      datasetMap.set(dataset.id, dataset);
    });
    datasetsById.value = datasetMap;

    if (evalRunsByDataset.value.size === 0) {
      const evalRunsResponse = await axios.get(`/api/eval-runs?skip=${skip}&take=${take}&leaderboards=true`);
      if (requestId !== datasetLoadRequestId.value) return;
      let runs = [];
      if (Array.isArray(evalRunsResponse.data?.evalRuns)) {
        runs = evalRunsResponse.data.evalRuns;
      } else if (Array.isArray(evalRunsResponse.data)) {
        runs = evalRunsResponse.data;
      } else if (Array.isArray(evalRunsResponse.data?.data)) {
        runs = evalRunsResponse.data.data;
      }
      evalRunsTotalItems.value = evalRunsResponse.data?.count ?? 0;
      buildEvalRunsIndex(runs);
    }

    await Promise.all(normalizedEvalDatasets.map((dataset) => fetchLeaderboard(dataset.id)));
    if (requestId !== datasetLoadRequestId.value) return;
    await Promise.all(
      normalizedEvalDatasets.map(async (dataset) => {
        try {
          const details = dataset;
          const totalTasks =
            details?.eval?.total_tasks ??
            details?.total_tasks ??
            (Array.isArray(details?.tasks) ? details.tasks.length : null);
          if (totalTasks != null) {
            evalTotalsByDataset.value.set(dataset.id, totalTasks);
          }
        } catch {
          // ignore missing eval details for list view
        }
      })
    );
    if (requestId !== datasetLoadRequestId.value) return;
    datasetTotalItems.value = evalRunsTotalItems.value;
    getDatasetRows();
  } catch (error) {
    addToast('Error loading datasets', 'error');
  } finally {
    if (requestId === datasetLoadRequestId.value) {
      isLoading.value.datasets = false;
    }
  }
};

const loadDatasetDetails = async () => {
  if (!datasetId.value) return;
  isLoading.value.tasks = true;
  try {
    const response = await fetchDatasetById(datasetId.value, version.value, true);
    datasetDetails.value = response;
    version.value = Number(response?.versions?.current || 1);
  } catch (error) {
    addToast(getFriendlyErrorMessage(error, 'Error loading dataset details'), 'error');
  } finally {
    isLoading.value.tasks = false;
  }
};

const openRunWithModels = async () => {
  if (!datasetId.value) {
    addToast('Open an eval before running a model benchmark.', 'error');
    return;
  }

  if (isLoading.value.tasks) {
    addToast('Please wait until the eval finishes loading.', 'error');
    return;
  }

  await openWithFeedback({
    errorFallback: 'Failed to open model benchmark. Please try again.',
    action: async ({ ensureCatalogAvailable }) => {
      const catalogReady = await ensureCatalogAvailable();
      if (!catalogReady) {
        return false;
      }
      isRunModelModalOpen.value = true;
      return true;
    },
  });
};

const handleTasksVersionChange = (nextVersion) => {
  if (!datasetId.value) return;
  const numericVersion = Number(nextVersion);
  if (!Number.isFinite(numericVersion)) return;
  router.push({
    query: {
      page: 'eval-annotate',
      datasetId: datasetId.value,
      version: numericVersion,
    },
  });
};

const loadTaskScores = async () => {
  if (!datasetId.value) return;
  if (datasetRows.value.length === 0) {
    await loadDatasetRows();
  }
  try {
    await ensureLeaderboard(Number(datasetId.value));
    const leaderboard = leaderboardByDataset.value.get(Number(datasetId.value)) || [];
    const llmIds = leaderboard
      .map((entry) => entry?.llm_id ?? entry?.llmId ?? entry?.llm?.id)
      .filter((id) => id != null)
      .map((id) => Number(id));

    if (llmIds.length === 0) {
      taskScoresById.value = {};
      taskPassesById.value = {};
      taskThresholdsById.value = {};
      return;
    }

    const currentDatasetId = datasetId.value;
    const currentVersion = version.value;
    const reportPromises = llmIds.map(async (llmId) => {
      const cacheKey = reportCacheKey(currentDatasetId, currentVersion, llmId);
      if (reportByLlmId.value.has(cacheKey)) {
        return { llmId, report: reportByLlmId.value.get(cacheKey) };
      }
      if (reportLoadingByLlmId.value.has(cacheKey)) {
        return reportLoadingByLlmId.value.get(cacheKey);
      }
      const request = axios
        .get(`/api/leaderboards/${currentDatasetId}_v${currentVersion}/report`, {
          params: { llm_id: llmId },
        })
        .then((response) => {
          const report = response?.data?.report || null;
          reportByLlmId.value.set(cacheKey, report);
          reportLoadingByLlmId.value.delete(cacheKey);
          return { llmId, report };
        })
        .catch((error) => {
          reportLoadingByLlmId.value.delete(cacheKey);
          throw error;
        });
      reportLoadingByLlmId.value.set(cacheKey, request);
      return request;
    });
    const reports = await Promise.all(reportPromises);

    const totalsByTask = {};
    const countsByTask = {};
    const passesByTask = {};
    const thresholdsByTask = {};

    reports.forEach(({ llmId, report }) => {
      const results = report?.results || [];
      const resultMap = buildResultMap(results);
      reportResultsByLlmId.value.set(Number(llmId), resultMap);
      resultMap.forEach((result, resolvedTaskId) => {
        const thresholdPercent = getResultThresholdPercent(result);
        if (thresholdPercent != null && thresholdsByTask[resolvedTaskId] == null) {
          thresholdsByTask[resolvedTaskId] = `${Math.round(thresholdPercent)}%`;
        }
        const scorePercent = getResultScorePercent(result);
        if (scorePercent != null) {
          totalsByTask[resolvedTaskId] = (totalsByTask[resolvedTaskId] || 0) + scorePercent;
          countsByTask[resolvedTaskId] = (countsByTask[resolvedTaskId] || 0) + 1;
        }

        const passFail = getPassFailForTask(Number(llmId), resolvedTaskId);
        if (passFail === 'pass') {
          passesByTask[resolvedTaskId] = (passesByTask[resolvedTaskId] || 0) + 1;
        }
      });
    });

    const scoreMap = {};
    const passMap = {};
    Object.keys(countsByTask).forEach((taskId) => {
      const count = countsByTask[taskId];
      const avg = count > 0 ? totalsByTask[taskId] / count : 0;
      scoreMap[taskId] = `${Math.round(avg)}%`;
      passMap[taskId] = `${passesByTask[taskId] || 0}/${count}`;
    });

    taskScoresById.value = scoreMap;
    taskPassesById.value = passMap;
    taskThresholdsById.value = thresholdsByTask;
  } catch (error) {
    taskScoresById.value = {};
    taskPassesById.value = {};
    taskThresholdsById.value = {};
  }
};

const buildTasksTable = () => {
  if (!datasetDetails.value?.tasks) {
    tasksTableRows.value = [];
    return;
  }

  const answers = Array.isArray(datasetDetails.value?.answers) ? datasetDetails.value.answers : [];
  tasksTableRows.value = datasetDetails.value.tasks.map((task) => {
    const matchedAnswer = answers.find((answer) => answer.task_id === task.task_id);
    let referenceFilesCount = 0;
    if (Array.isArray(task.reference_file)) {
      referenceFilesCount = task.reference_file.length;
    } else if (task.reference_file) {
      referenceFilesCount = 1;
    }
    return {
    view: 'view',
    task_id: task.task_id,
    task_preview: truncateText(task.task_prompt, 100),
    task_preview_short: truncateText(task.task_prompt, 35),
    criteria_count: matchedAnswer?.criteria?.length ?? 0,
    reference_files: referenceFilesCount,
    pass_threshold: taskThresholdsById.value[task.task_id] || '—',
    avg_performance: taskScoresById.value[task.task_id] || '—',
    passed: taskPassesById.value[task.task_id] || '—',
    };
  });
};

const handleDatasetRowRedirect = (item) => {
  setTasksView(item);
};

const handleTaskRowRedirect = (item) => {
  setTaskView(item);
};

const loadTags = async () => {
  try {
    const response = await fetchTags();
    tags.value = response.filter((tag) => {
      const typeName = String(tag?.tag_type?.type || '').toLowerCase();
      return Boolean(tag?.tag_type?.is_annotation) || typeName === 'annotation' || typeName === 'annotations';
    });
  } catch (error) {
    tags.value = [];
  }
};

const loadLlms = async () => {
  try {
    const response = await fetchLLMs();
    const llms = response?.data || [];
    const nameMap = new Map();
    const idMap = new Map();
    llms.forEach((llm) => {
      const nameKey = normalizeName(llm.name || llm.hostname);
      nameMap.set(nameKey, llm);
      if (llm.id != null) {
        idMap.set(Number(llm.id), llm);
      }
    });
    llmsByName.value = nameMap;
    llmsById.value = idMap;
  } catch (error) {
    llmsByName.value = new Map();
    llmsById.value = new Map();
  }
};

const formatHarnessName = (name) => {
  if (!name) return '';
  return name.replace(/(^|-)(\w)/g, (_, sep, ch) => (sep === '-' ? '-' : '') + ch.toUpperCase())
    .replace(/-/g, '-');
};

const formatModelName = (name) => {
  if (!name) return '';
  const parts = name.split('/');
  const modelPart = parts[parts.length - 1];
  return modelPart.replace(/(^|-)(\w)/g, (_, sep, ch) => (sep === '-' ? '-' : '') + ch.toUpperCase());
};

const buildModelChips = () => {
  const leaderboard = leaderboardByDataset.value.get(Number(datasetId.value)) || [];
  const chips = leaderboard
    .map((entry) => {
      const leaderboardLlmId =
        entry?.llm_id ??
        entry?.llmId ??
        entry?.llm?.id ??
        null;
      if (leaderboardLlmId) {
        const chipName = isAgenticEval.value && entry.agent_harness
          ? `${formatHarnessName(entry.agent_harness)} x ${formatModelName(entry.model_name)}`
          : entry.model_name;
        return {
          id: Number(leaderboardLlmId),
          name: chipName,
          icon: getLlmIcon(Number(leaderboardLlmId), entry.model_name),
        };
      }
      const match = llmsByName.value.get(normalizeName(entry.model_name));
      if (!match) return null;
      return {
        id: Number(match.id),
        name: match.name,
        icon: match.icon || null,
      };
    })
    .filter(Boolean);

  modelChips.value = chips;
  if (!selectedLlmId.value && chips.length > 0) {
    selectedLlmId.value = chips[0].id;
  }
};

const closeTrajectoryStream = () => {
  if (trajectoryEventSource) {
    trajectoryEventSource.close();
    trajectoryEventSource = null;
  }
};

const applyModelResponsePayload = (payload) => {
  modelResponse.value = normalizeModelText(payload?.prompt?.model_response || '');
  modelThinking.value = normalizeModelText(payload?.prompt?.thinking || '');

  if (payload?.eval_type === 'agentic') {
    agentTrajectory.value = payload.prompt?.trajectory || null;
    agentInfo.value = payload.prompt?.agent_info || null;
    agentRunInProgress.value = Boolean(payload?.in_progress);
  } else {
    agentTrajectory.value = null;
    agentInfo.value = null;
    agentRunInProgress.value = false;
    if (responseTab.value === 'trajectory') {
      responseTab.value = 'response';
    }
  }
};

const startTrajectoryStream = () => {
  if (!datasetId.value || !selectedLlmId.value || !taskId.value) return;

  closeTrajectoryStream();

  const baseUrl = getApiBaseUrl();
  const query = new URLSearchParams({
    llm_id: String(selectedLlmId.value),
    task_id: String(taskId.value),
  });
  const streamUrl = `${baseUrl}/api/leaderboards/${datasetId.value}_v${version.value}/model-responses/stream?${query.toString()}`;
  const source = new EventSource(streamUrl, { withCredentials: true });
  trajectoryEventSource = source;

  const handleSnapshot = (event) => {
    try {
      const payload = JSON.parse(event.data || '{}');
      if (payload) {
        applyModelResponsePayload(payload);
      }
    } catch (error) {
      // Ignore malformed stream chunks and keep listening.
    }
  };

  source.addEventListener('snapshot', handleSnapshot);
  source.addEventListener('done', (event) => {
    handleSnapshot(event);
    agentRunInProgress.value = false;
    if (trajectoryEventSource === source) {
      closeTrajectoryStream();
    }
  });
  source.onmessage = handleSnapshot;
  source.onerror = () => {
    if (trajectoryEventSource === source) {
      closeTrajectoryStream();
      agentRunInProgress.value = false;
    }
  };
};

const loadModelResponse = async () => {
  if (!datasetId.value || !selectedLlmId.value || !taskId.value) return;
  isLoading.value.task = true;
  closeTrajectoryStream();
  agentTrajectory.value = null;
  try {
    await ensureReportForLlm(selectedLlmId.value);
    const response = await axios.get(
      `/api/leaderboards/${datasetId.value}_v${version.value}/model-responses`,
      {
        params: {
          llm_id: selectedLlmId.value,
          task_id: taskId.value,
        },
      }
    );

    applyModelResponsePayload(response.data);

    if (response.data?.eval_type === 'agentic') {
      if (responseTab.value === 'reasoning') {
        responseTab.value = 'response';
      }
      startTrajectoryStream();
      loadHarborEnvironment();
    }

    await loadAnnotation();
  } catch (error) {
    modelResponse.value = '';
    modelThinking.value = '';
    agentTrajectory.value = null;
    agentInfo.value = null;
    agentRunInProgress.value = false;
    harborEnvironment.value = { task_toml: null, dockerfile: null };
    closeTrajectoryStream();
  } finally {
    isLoading.value.task = false;
  }
};

const loadHarborEnvironment = async () => {
  if (!datasetId.value || !taskId.value || !isAgenticEval.value) return;
  isLoading.value.environment = true;
  try {
    const response = await axios.get(
      `/api/leaderboards/${datasetId.value}_v${version.value}/harbor-environment`,
      { params: { task_id: taskId.value } }
    );
    harborEnvironment.value = {
      task_toml: response.data?.task_toml || null,
      dockerfile: response.data?.dockerfile || null,
    };
  } catch {
    harborEnvironment.value = { task_toml: null, dockerfile: null };
  } finally {
    isLoading.value.environment = false;
  }
};

const loadAnnotation = async () => {
  if (!datasetId.value || !taskId.value || !selectedLlmId.value) return;
  try {
    const annotation = await fetchAnnotation(
      datasetId.value,
      version.value,
      taskId.value,
      selectedLlmId.value
    );
    annotationByLlmId.value[selectedLlmId.value] = annotation;
    notes.value = annotation?.notes_array || [];
    selectedTags.value = normalizeTagIds(annotation?.tags || []);
  } catch (error) {
    notes.value = [];
    selectedTags.value = [];
  }
};

const setDatasetView = () => {
  router.push({ query: { page: 'eval-annotate' } });
};

const setTasksView = (row) => {
  router.push({
    query: {
      page: 'eval-annotate',
      datasetId: row.dataset_id,
      version: row.version_number,
    },
  });
};

const setTaskView = (row) => {
  router.push({
    query: {
      page: 'eval-annotate',
      datasetId: datasetId.value,
      version: version.value,
      taskId: row.task_id,
    },
  });
};


const addNote = (selectionText = '') => {
  const selectionRange = selectionText
    ? buildSelectionRange(modelResponse.value, selectionText)
    : [[0, 0]];
  notes.value.push({
    selection_range: selectionRange,
    selection_text: selectionText ? [selectionText] : [],
    notes: [''],
  });
};

const buildSelectionRange = (fullText, selectedText) => {
  const startIndex = fullText.indexOf(selectedText);
  if (startIndex < 0) return [[0, 0]];
  const endIndex = startIndex + selectedText.length;
  const start = getLineChar(fullText, startIndex);
  const end = getLineChar(fullText, endIndex);
  return [start, end];
};

const getLineChar = (text, index) => {
  const before = text.slice(0, index);
  const lines = before.split('\n');
  const line = lines.length - 1;
  const char = lines[lines.length - 1].length;
  return [line, char];
};

const getKatexSourceFromSelection = (selection) => {
  if (!selection || selection.rangeCount === 0) return '';
  const range = selection.getRangeAt(0);
  const containers = [range.startContainer, range.endContainer];
  for (const node of containers) {
    const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    if (!element) continue;
    const katexRoot = element.closest?.('.katex, .katex-display');
    if (!katexRoot) continue;
    const annotation = katexRoot.querySelector?.('annotation[encoding="application/x-tex"]');
    const source = annotation?.textContent?.trim();
    if (source) return source;
  }
  return '';
};

const getRawSelectionText = (selection) => {
  const text = selection?.toString?.().trim() || '';
  const katexSource = getKatexSourceFromSelection(selection);
  if (katexSource) return katexSource;
  if (!text) return '';
  const raw = responseTab.value === 'reasoning' ? modelThinking.value || '' : modelResponse.value || '';
  if (raw.includes(text)) return text;
  return text;
};

const handleSelection = () => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return;
  const rawSelection = getRawSelectionText(selection);
  if (!rawSelection) return;
  addNote(rawSelection);
};

const toggleTag = async (tagId) => {
  const numericId = Number(tagId);
  if (selectedTags.value.includes(numericId)) {
    selectedTags.value = selectedTags.value.filter((id) => id !== numericId);
  } else {
    selectedTags.value = [...selectedTags.value, numericId];
  }

  if (bestResponseTagId.value && numericId === bestResponseTagId.value) {
    await clearBestResponseOnOtherModels(numericId);
  }
};

const clearBestResponseOnOtherModels = async (bestId) => {
  const currentLlmId = selectedLlmId.value;
  if (!currentLlmId) return;
  const entries = Object.entries(annotationByLlmId.value);
  for (const [llmKey, annotation] of entries) {
    const llmIdNum = Number(llmKey);
    if (llmIdNum === currentLlmId) continue;
    if (!annotation?.id || !Array.isArray(annotation.tags)) continue;
    if (!annotation.tags.includes(bestId)) continue;
    const updatedTags = annotation.tags.filter((id) => Number(id) !== bestId);
    try {
      await updateAnnotation(annotation.id, { tags: updatedTags });
      annotationByLlmId.value[llmIdNum] = {
        ...annotation,
        tags: updatedTags,
      };
    } catch (error) {
      addToast('Failed to clear Best Response on another model', 'error');
    }
  }
};

const saveAnnotation = async () => {
  if (!datasetId.value || !taskId.value) return;
  isSaving.value = true;
  try {
    syncCurrentAnnotationDraft();
    const savedLlmIds = await saveAllAnnotations();
    addToast('Annotations saved', 'success');
    if (datasetId.value) {
      const savedNames = (savedLlmIds || [])
        .map((id) => modelChips.value.find((chip) => chip.id === id)?.name || String(id))
        .filter(Boolean);
      const count = savedNames.length;
      const modelsText = count > 0 ? savedNames.join(', ') : '—';
      alertMessage.value = `${count} annotations saved for models ${modelsText} on task ${taskId.value}.<br/><br/>Would you like to update your eval based on these responses?`;
      showSaveAlert.value = true;
    }
  } catch (error) {
    addToast('Failed to save annotations', 'error');
  } finally {
    isSaving.value = false;
  }
};

const handleSaveAlertConfirm = () => {
  showSaveAlert.value = false;
  if (!datasetId.value) return;
  router.push(`/creator-profile?page=eval-builder&datasetId=${datasetId.value}`);
};

const handleSaveAlertClose = () => {
  showSaveAlert.value = false;
};

watch(viewMode, async (mode) => {
  if (mode !== 'task') {
    closeTrajectoryStream();
    agentRunInProgress.value = false;
  }
  if (mode === 'datasets') {
    datasetPage.value = 1;
    await loadDatasetRows();
  }
  if (mode === 'tasks') {
    tasksPage.value = 1;
    tasksTab.value = 'summary';
    await loadDatasetDetails();
    await ensureLeaderboard(Number(datasetId.value));
    await loadLlms();
    await loadTaskScores();
    buildTasksTable();
  }
  if (mode === 'task') {
    responseTab.value = 'response';
    detailsTab.value = 'notes';
    await loadDatasetDetails();
    await ensureLeaderboard(Number(datasetId.value));
    await loadLlms();
    buildModelChips();
    await loadModelResponse();
    await loadAllAnnotations();
    syncRankingFromAnnotations();
  }
});

watch([version, datasetDetails], () => {
  selectedTasksVersion.value = Number(version.value || 1);
});

watch(version, async (next, prev) => {
  if (next === prev) return;
  if (!datasetId.value) return;
  if (viewMode.value === 'tasks') {
    tasksTab.value = 'summary';
    await loadDatasetDetails();
    await ensureLeaderboard(Number(datasetId.value));
    await loadLlms();
    await loadTaskScores();
    buildTasksTable();
  }
  if (viewMode.value === 'task') {
    responseTab.value = 'response';
    detailsTab.value = 'notes';
    await loadDatasetDetails();
    await ensureLeaderboard(Number(datasetId.value));
    await loadLlms();
    buildModelChips();
    await loadModelResponse();
    await loadAllAnnotations();
    syncRankingFromAnnotations();
  }
});

watch(selectedLlmId, async (next, prev) => {
  if (prev) {
    syncCurrentAnnotationDraft(prev);
  }
  if (viewMode.value === 'task') {
    await loadModelResponse();
  }
  lastSelectedLlmId.value = next || null;
});

watch([taskId, selectedLlmId], () => {
  judgeExpandedByKey.value = {};
});

watch(rankedLlmIds, () => {
  if (!rankTagsReady.value || !selectedLlmId.value) return;
  const selectedRankIndex = rankedLlmIds.value.findIndex((id) => id === selectedLlmId.value);
  selectedTags.value = buildTagsWithRank(selectedTags.value, selectedRankIndex);
});

watch(detailsTab, () => {
  if (annotateDetailBodyRef.value) {
    annotateDetailBodyRef.value.scrollTop = 0;
  }
});

watch([datasetId, version], () => {
  closeTrajectoryStream();
  agentRunInProgress.value = false;
  reportByLlmId.value = new Map();
  reportLoadingByLlmId.value = new Map();
  reportResultsByLlmId.value = new Map();
  taskScoresById.value = {};
  taskPassesById.value = {};
  taskThresholdsById.value = {};
  rankedLlmIds.value = [null, null, null];
  draggedLlmId.value = null;
});

watch([datasetDetails, taskScoresById, taskThresholdsById], () => {
  if (viewMode.value === 'tasks') {
    buildTasksTable();
  }
});


onMounted(async () => {
  await loadTags();
  if (viewMode.value === 'datasets') {
    await loadDatasetRows();
  } else if (viewMode.value === 'tasks') {
    tasksTab.value = 'summary';
    await loadDatasetDetails();
    await ensureLeaderboard(Number(datasetId.value));
    await loadLlms();
    await loadTaskScores();
    buildTasksTable();
  } else if (viewMode.value === 'task') {
    await loadDatasetDetails();
    await ensureLeaderboard(Number(datasetId.value));
    const leaderboard = leaderboardByDataset.value.get(Number(datasetId.value)) || [];
    const hasLlmIds = leaderboard.some(
      (entry) => entry?.llm_id != null || entry?.llmId != null || entry?.llm?.id != null
    );
    if (!hasLlmIds || llmsById.value.size === 0) {
      await loadLlms();
    }
    buildModelChips();
    await loadModelResponse();
    await loadAllAnnotations();
    syncRankingFromAnnotations();
  }
});

watch(datasetPage, async () => {
  if (viewMode.value === 'datasets') {
    await loadDatasetRows();
  }
});

onBeforeUnmount(() => {
  closeTrajectoryStream();
});
</script>

<template>
  <div class="h-full min-h-0 flex flex-col gap-3" :data-testid="props.testId">
    <Alert
      v-if="showSaveAlert"
      :show="showSaveAlert"
      :title="alertTitle"
      :message="alertMessage"
      :type="alertType"
      confirmText="Yes, update eval"
      cancelText="No, stay here"
      @confirm="handleSaveAlertConfirm"
      @close="handleSaveAlertClose"
      :showCloseBtn="false"
      dataTestId="annotate-save-update-eval-alert"
    ></Alert>
    <RunModelBenchmarkModal
      :isOpen="isRunModelModalOpen"
      :dataset-id="datasetId"
      :version="version"
      :dataset-name="datasetDetails?.name || ''"
      @close="isRunModelModalOpen = false"
    />
    <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div
        v-if="viewMode === 'datasets'"
        data-testid="annotate-datasets-view"
        class="h-full min-h-0 flex flex-col overflow-auto"
      >
      <EvalTable
        :data="sortedDatasetRows"
        :columns="datasetColumns"
        :totalItems="datasetTotalItems"
        :showPagination="true"
        :currentPage="datasetPage"
        :itemsPerPage="tableItemsPerPage"
        :showItemsPerPage="false"
        :showSearch="false"
        :isLoading="isLoading.datasets"
        :serverPagination="true"
        loadingText="Loading evals..."
        keyField="dataset_id"
        title="Annotate"
        subtitle="Click on an eval dataset to see model responses to your tasks."
        emptyMessage="No eval datasets found"
        :redirectableColumns="datasetRowRedirects"
        :sortBy="datasetSortBy"
        :sortOrder="datasetSortOrder"
        @sort="handleDatasetSort"
        @update:currentPage="datasetPage = $event"
        @redirect="handleDatasetRowRedirect"
        class="hidden md:block"
      >
        <template #column-view="{ item }">
          <button
            class="text-echoes-primary hover:text-echoes-primary/80 transition-colors duration-200"
            :data-testid="`annotate-dataset-view-${item.dataset_id}`"
            @click="setTasksView(item)"
          >
            <i class="uil uil-eye text-lg"></i>
          </button>
        </template>
        <template #column-dataset_name="{ item }">
          <button
            class="text-left text-white hover:text-echoes-primary block max-w-[180px] truncate"
            :data-testid="`annotate-dataset-${item.dataset_id}`"
            @click="setTasksView(item)"
            :title="item.dataset_name"
          >
            {{ truncateText(item.dataset_name, 28) }}
          </button>
        </template>
        <template #column-top_model="{ item }">
          <div class="explore-chip explore-chip--solid">
            <div class="explore-chip__inner">
              <div class="explore-chip__icon-circle">
                <img
                  v-if="getLlmIcon(item.top_model_id, item.top_model)"
                  :src="getLlmIcon(item.top_model_id, item.top_model)"
                  :alt="item.top_model"
                />
                <i v-else class="mdi mdi-robot"></i>
              </div>
              <span class="explore-chip__label" :title="item.top_model">{{ item.top_model }}</span>
            </div>
          </div>
        </template>
      </EvalTable>
      </div>

    <div
      v-else-if="viewMode === 'tasks'"
      class="h-full min-h-0 flex flex-col gap-4 overflow-auto"
      data-testid="annotate-tasks-view"
    >
      <div class="flex flex-wrap items-center gap-4">
        <button class="back-button" data-testid="annotate-back-to-datasets" @click="setDatasetView">
          <i class="mdi mdi-chevron-left text-lg"></i>
          Back to datasets
        </button>
        <div class="flex flex-wrap items-center gap-3 text-sm text-white/80">
        <div class="font-medium text-white">
          {{ datasetDetails?.name || `Dataset ${datasetId}` }} · v{{ version }}
        </div>
          <div class="annotate-version-select">
            <SelectInput
              :id="`annotate-version-${datasetId || 'current'}`"
              v-model="selectedTasksVersion"
              :options="datasetVersionOptions"
              :hide-search="true"
              :disabled="datasetVersionOptions.length === 0 || isLoading.tasks"
              dataTestId="annotate-dataset-version-select"
              @update:modelValue="handleTasksVersionChange"
            />
          </div>
          <button
            type="button"
            class="inline-flex items-center px-3 py-1.5 rounded-lg bg-echoes-primary text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="annotate-run-with-models"
            :disabled="isOpeningRunModal || isLoading.tasks"
            @click="openRunWithModels"
          >
            <i
              v-if="isOpeningRunModal || isLoading.tasks"
              class="mdi mdi-loading mdi-spin mr-1 text-base"
            ></i>
            {{ isOpeningRunModal ? 'Loading...' : 'Run with models' }}
          </button>
        </div>
      </div>

      <div
        class="inline-flex items-center gap-2 p-0.5 rounded-full bg-black/20 border border-white/10 mt-1 w-fit"
        data-testid="annotate-task-tabs"
      >
        <button
          class="px-2.5 py-1 text-sm leading-5 font-normal rounded-xl transition-colors duration-200 bg-transparent border border-transparent"
          :class="tasksTab === 'summary'
            ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
            : 'text-white/60 hover:text-white hover:bg-white/5'"
          data-testid="annotate-tab-summary"
          @click="tasksTab = 'summary'"
        >
          Summary
        </button>
        <button
          class="px-2.5 py-1 text-sm leading-5 font-normal rounded-xl transition-colors duration-200 bg-transparent border border-transparent"
          :class="tasksTab === 'grid'
            ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
            : 'text-white/60 hover:text-white hover:bg-white/5'"
          data-testid="annotate-tab-passfail"
          @click="tasksTab = 'grid'"
        >
          Pass/Fail Grid
        </button>
      </div>

      <div v-if="tasksTab === 'summary'" class="pt-2">
        <EvalTable
          :data="sortedTasksTableRows"
          :columns="tasksColumns"
          :totalItems="sortedTasksTableRows.length"
          :showPagination="true"
          :currentPage="tasksPage"
          :itemsPerPage="tableItemsPerPage"
          :showItemsPerPage="false"
          :showSearch="false"
          :isLoading="isLoading.tasks"
          loadingText="Loading tasks..."
          keyField="task_id"
          title="Tasks"
          subtitle="Click on a task to view model responses."
          emptyMessage="No tasks found"
          :redirectableColumns="taskRowRedirects"
          :sortBy="taskSortBy"
          :sortOrder="taskSortOrder"
          @sort="handleTaskSort"
          @update:currentPage="tasksPage = $event"
          @redirect="handleTaskRowRedirect"
          class="hidden md:block"
        >
          <template #column-view="{ item }">
            <button
              class="text-echoes-primary hover:text-echoes-primary/80 transition-colors duration-200"
              :data-testid="`annotate-task-view-${item.task_id}`"
              @click="setTaskView(item)"
            >
              <i class="uil uil-eye text-lg"></i>
            </button>
          </template>
          <template #column-task_id="{ item }">
            <button
              class="text-left text-white hover:text-echoes-primary"
              :data-testid="`annotate-task-${item.task_id}`"
              @click="setTaskView(item)"
            >
              {{ item.task_id }}
            </button>
          </template>
        </EvalTable>
      </div>

      <div v-else class="passfail-grid mt-2" data-testid="annotate-passfail-grid">
        <div class="passfail-grid__table">
          <table>
            <thead>
              <tr>
                <th class="passfail-grid__cell passfail-grid__cell--left"></th>
                <th class="passfail-grid__cell passfail-grid__cell--left">Task ID</th>
                <th class="passfail-grid__cell passfail-grid__cell--left">Task Preview</th>
                <th
                  v-for="model in taskGridColumns"
                  :key="model.id"
                  class="passfail-grid__cell passfail-grid__cell--center"
                >
                  <div class="explore-chip explore-chip--solid passfail-chip--model">
                    <div class="explore-chip__inner">
                      <div class="explore-chip__icon-circle">
                        <img v-if="model.icon" :src="model.icon" :alt="model.name" />
                        <i v-else class="mdi mdi-robot"></i>
                      </div>
                      <span class="explore-chip__label" :title="model.name">{{ model.name }}</span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="task in tasksTableRows" :key="task.task_id">
              <tr>
                <td class="passfail-grid__cell passfail-grid__cell--left">
                  <button
                    class="passfail-grid__expand-btn"
                    :data-testid="`annotate-grid-expand-${task.task_id}`"
                    @click="toggleTaskExpanded(task.task_id)"
                  >
                    <i
                      class="uil"
                      :class="isTaskExpanded(task.task_id) ? 'uil-angle-down' : 'uil-angle-right'"
                    ></i>
                  </button>
                </td>
                <td class="passfail-grid__cell passfail-grid__cell--left">
                  <button
                    class="text-left text-white hover:text-echoes-primary"
                    :data-testid="`annotate-grid-task-${task.task_id}`"
                    @click="setTaskView(task)"
                  >
                    {{ task.task_id }}
                  </button>
                </td>
                <td class="passfail-grid__cell passfail-grid__cell--left">
                  {{ task.task_preview_short || task.task_preview }}
                </td>
                <td
                  v-for="model in taskGridColumns"
                  :key="`${task.task_id}-${model.id}`"
                  class="passfail-grid__cell passfail-grid__cell--center passfail-grid__cell--clickable"
                  :data-testid="`annotate-grid-cell-${task.task_id}-${model.id}`"
                  @click="setTaskView(task)"
                >
                  <span
                    v-if="getPassFailForTask(model.id, task.task_id) === 'pass'"
                    class="passfail-chip passfail-chip--pass"
                  >
                    PASS
                  </span>
                  <span
                    v-else-if="getPassFailForTask(model.id, task.task_id) === 'fail'"
                    class="passfail-chip passfail-chip--fail"
                  >
                    FAIL
                  </span>
                  <span v-else class="passfail-chip passfail-chip--empty">—</span>
                </td>
              </tr>
              <tr v-if="isTaskExpanded(task.task_id)">
                <td
                  class="passfail-grid__cell passfail-grid__expanded"
                  :colspan="3 + taskGridColumns.length"
                >
                  <div
                    class="criterion-grid"
                    :style="{ '--criterion-columns': taskGridColumns.length }"
                  >
                    <div class="criterion-grid__header">Criteria</div>
                    <div
                      v-for="model in taskGridColumns"
                      :key="`header-${task.task_id}-${model.id}`"
                      class="criterion-grid__header criterion-grid__header--center"
                    >
                      <div class="explore-chip explore-chip--solid passfail-chip--model">
                        <div class="explore-chip__inner">
                          <div class="explore-chip__icon-circle">
                            <img v-if="model.icon" :src="model.icon" :alt="model.name" />
                            <i v-else class="mdi mdi-robot"></i>
                          </div>
                          <span class="explore-chip__label" :title="model.name">{{ model.name }}</span>
                        </div>
                      </div>
                    </div>
                    <template
                      v-for="(criterion, index) in getCriteriaForTask(task.task_id)"
                      :key="`${task.task_id}-${getCriterionKey(criterion)}`"
                    >
                      <div class="criterion-grid__cell">
                        <div class="criterion-grid__title-row">
                          <span class="criterion-grid__title" :title="criterion.name">
                            {{ index + 1 }}. {{ criterion.name }}
                          </span>
                          <span
                            v-if="criterion.weight != null"
                            class="criterion-chip criterion-chip--weight"
                          >
                            {{ Number(criterion.weight) }}%
                          </span>
                        </div>
                      </div>
                      <div
                        v-for="model in taskGridColumns"
                        :key="`${task.task_id}-${criterion.id}-${model.id}`"
                        class="criterion-grid__cell criterion-grid__cell--center"
                      >
                        <span
                          v-if="getCriterionVerdictForModel(task.task_id, model.id, criterion)"
                          :class="[
                            'criterion-chip',
                            getCriterionVerdictForModel(task.task_id, model.id, criterion) === 'pass' ? 'criterion-chip--pass' : '',
                            getCriterionVerdictForModel(task.task_id, model.id, criterion) === 'fail' ? 'criterion-chip--fail' : '',
                            getCriterionVerdictForModel(task.task_id, model.id, criterion) === 'flagged' ? 'criterion-chip--flagged' : ''
                          ]"
                        >
                          {{ getCriterionVerdictForModel(task.task_id, model.id, criterion) }}
                        </span>
                        <span v-else class="criterion-chip criterion-chip--weight">—</span>
                      </div>
                    </template>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else class="annotate-task-view flex flex-col gap-3" data-testid="annotate-task-view">
      <div class="annotate-task-header">
        <div class="flex items-center gap-3">
          <button
            class="back-button"
            data-testid="annotate-back-to-tasks"
            @click="setTasksView({ dataset_id: datasetId, version_number: version })"
          >
            <i class="mdi mdi-chevron-left text-lg"></i>
            Back to tasks
          </button>
          <div class="text-sm text-white/70">
            {{ datasetDetails?.name || `Dataset ${datasetId}` }} · v{{ version }} · Task {{ taskId }}
          </div>
        </div>
        <button
          :class="[
            'rounded-full py-1.5 px-4 font-semibold text-sm transition-all duration-200 flex items-center gap-2',
            isSaving
              ? 'bg-green-600/50 border border-green-600/50 text-white/70 cursor-not-allowed'
              : 'bg-green-600 border border-green-600 text-white cursor-pointer hover:bg-green-700 hover:border-green-700'
          ]"
          :disabled="isSaving"
          data-testid="annotate-save"
          @click="saveAnnotation"
        >
          <div v-if="isSaving" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          <span>{{ isSaving ? 'Saving...' : 'Save Annotations' }}</span>
          <i class="mdi mdi-chevron-right-circle-outline text-base"></i>
        </button>
      </div>

      <div class="annotate-task-body">
        <div class="annotate-chip-card annotate-model-card" data-testid="annotate-model-selector">
          <div class="annotate-model-row">
            <div class="annotate-chip-card__title">{{ isAgenticEval ? 'Select Agent:' : 'Select Model:' }}</div>
            <div class="explore-chip-row explore-chip-row--compact">
            <button
              v-for="chip in modelChips"
              :key="chip.id"
              type="button"
              :class="[
                'explore-chip',
                'explore-chip--lg',
                chip.id === selectedLlmId ? 'explore-chip--active' : ''
              ]"
              :data-testid="`annotate-model-${chip.id}`"
              @click="selectedLlmId = chip.id"
            >
              <div class="explore-chip__inner">
                <div class="explore-chip__icon-circle">
                  <img v-if="chip.icon" :src="chip.icon" :alt="chip.name" />
                  <i v-else class="mdi mdi-robot"></i>
                </div>
                <span class="explore-chip__label" :title="chip.name">{{ chip.name }}</span>
              </div>
            </button>
          </div>
          </div>
        </div>

        <div class="annotate-grid">
          <div class="annotate-response" data-testid="annotate-model-response">
            <div class="annotate-response__header">
              <div
                class="inline-flex items-center gap-2 p-0.5 rounded-lg bg-black/20 border border-white/10 w-fit"
                data-testid="annotate-response-tabs"
              >
                <template v-if="isAgenticEval">
                  <button
                    class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                    :class="responseTab === 'response'
                      ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                      : 'text-white/60 hover:text-white hover:bg-white/5'"
                    data-testid="annotate-response-tab"
                    @click="responseTab = 'response'"
                  >
                    Agent Answer
                  </button>
                  <button
                    class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                    :class="responseTab === 'trajectory'
                      ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                      : 'text-white/60 hover:text-white hover:bg-white/5'"
                    data-testid="annotate-trajectory-tab"
                    @click="responseTab = 'trajectory'"
                  >
                    Agent Trajectory
                  </button>
                </template>
                <template v-else>
                  <button
                    class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                    :class="responseTab === 'response'
                      ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                      : 'text-white/60 hover:text-white hover:bg-white/5'"
                    data-testid="annotate-response-tab"
                    @click="responseTab = 'response'"
                  >
                    Model Response
                  </button>
                  <button
                    class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                    :class="responseTab === 'reasoning'
                      ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                      : 'text-white/60 hover:text-white hover:bg-white/5'"
                    data-testid="annotate-reasoning-tab"
                    @click="responseTab = 'reasoning'"
                  >
                    Model Reasoning
                  </button>
                </template>
              </div>
              <span
                v-if="selectedTaskVerdict"
                :class="[
                  'criterion-chip',
                  selectedTaskVerdict === 'pass' ? 'criterion-chip--pass' : '',
                  selectedTaskVerdict === 'fail' ? 'criterion-chip--fail' : '',
                  selectedTaskVerdict === 'flagged' ? 'criterion-chip--flagged' : ''
                ]"
                data-testid="annotate-task-verdict-chip"
              >
                {{ selectedTaskVerdict }}
              </span>
            </div>
            <div class="annotate-response-body" @mouseup="handleSelection">
              <MarkdownRenderer
                v-if="responseTab === 'response'"
                :model-value="modelResponse"
                :content-only="true"
              />
              <TrajectoryViewer
                v-else-if="responseTab === 'trajectory'"
                :trajectory="agentTrajectory"
                :agent-info="agentInfo"
                :is-in-progress="agentRunInProgress"
              />
              <MarkdownRenderer
                v-else
                :model-value="modelThinkingDisplay"
                :content-only="true"
              />
            </div>
          </div>

          <div class="annotate-details" data-testid="annotate-task-details">
            <div
              class="inline-flex items-center justify-between gap-2 p-0.5 rounded-lg bg-black/20 border border-white/10 w-full"
              data-testid="annotate-details-tabs"
            >
              <button
                class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                :class="detailsTab === 'notes'
                  ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                  : 'text-white/60 hover:text-white hover:bg-white/5'"
                data-testid="annotate-notes-tab"
                @click="detailsTab = 'notes'"
              >
                Add Notes
              </button>
              <button
                class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                :class="detailsTab === 'prompt'
                  ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                  : 'text-white/60 hover:text-white hover:bg-white/5'"
                data-testid="annotate-prompt-tab"
                @click="detailsTab = 'prompt'"
              >
                Task Prompt
              </button>
              <button
                v-if="isAgenticEval"
                class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                :class="detailsTab === 'environment'
                  ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                  : 'text-white/60 hover:text-white hover:bg-white/5'"
                data-testid="annotate-environment-tab"
                @click="detailsTab = 'environment'"
              >
                Environment
              </button>
              <button
                class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                :class="detailsTab === 'answer'
                  ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                  : 'text-white/60 hover:text-white hover:bg-white/5'"
                data-testid="annotate-answer-tab"
                @click="detailsTab = 'answer'"
              >
                Answer
              </button>
              <button
                class="px-3 2xl:px-4 py-2 text-xs 2xl:text-sm leading-5 font-normal rounded-md transition-colors duration-200 bg-transparent border border-transparent"
                :class="detailsTab === 'criteria'
                  ? 'bg-white/10 border border-echoes-primary text-echoes-primary'
                  : 'text-white/60 hover:text-white hover:bg-white/5'"
                data-testid="annotate-criteria-tab"
                @click="detailsTab = 'criteria'"
              >
                Grading Criteria
              </button>
            </div>
          <div ref="annotateDetailBodyRef" class="annotate-detail-body">
            <div v-if="detailsTab === 'notes'" class="annotate-section">
              <div class="notes-container" data-testid="annotate-notes">
                <div class="notes-header">
                  <span>Notes</span>
                  <v-tooltip location="top" max-width="320">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary pointer-events-auto"
                        v-bind="tooltipProps"
                      >
                        <path
                          fill="currentColor"
                          d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z"
                        ></path>
                      </svg>
                    </template>
                    <span>Highlight a selection from the model's output and add commentary.</span>
                  </v-tooltip>
                </div>
                <div class="notes-body">
                  <div v-if="notes.length === 0" class="notes-placeholder">
                    Highlight part of the model response you'd like to annotate...
                  </div>
                  <div v-for="(note, index) in notes" :key="index" class="note-row">
                    <div class="note-index">{{ index + 1 }}</div>
                    <div class="note-content">
                      <div class="note-selection">
                        {{ note.selection_text?.[0] || '<selection>' }}
                      </div>
                      <textarea
                        v-model="note.notes[0]"
                        class="note-textarea"
                        :data-testid="`annotate-note-${index}`"
                        placeholder="Add your note"
                      ></textarea>
                    </div>
                    <button
                      class="note-remove"
                      :data-testid="`annotate-note-remove-${index}`"
                      @click="notes.splice(index, 1)"
                    >
                      Remove
                    </button>
                  </div>
                  <button class="note-add" data-testid="annotate-note-add" @click="addNote()">+</button>
                </div>
              </div>

            <div class="annotate-chip-card mt-4" data-testid="annotate-ranking">
              <div class="annotate-chip-card__title">Rank Responses (Top 3)</div>
              <div v-if="!rankTagsReady" class="ranking-empty">
                Ranking tags (Rank 1/2/3) are not available.
              </div>
              <div v-else class="ranking-grid">
                <div class="ranking-slots">
                  <div
                    v-for="(chip, index) in rankedChipsByIndex"
                    :key="`rank-slot-${index}`"
                    class="ranking-slot"
                    :data-testid="`annotate-rank-slot-${index + 1}`"
                    @dragover.prevent
                    @drop="handleDropRank(index)"
                  >
                    <div class="ranking-slot__label">
                      <span>Rank {{ index + 1 }}</span>
                      <span v-if="!chip" class="ranking-slot__hint">Drop a model here</span>
                    </div>
                    <div v-if="chip" class="ranking-slot__chip">
                      <div
                        class="explore-chip explore-chip--lg ranking-chip"
                        draggable="true"
                        :data-testid="`annotate-rank-chip-${chip.id}`"
                        @dragstart="handleDragStart(chip.id)"
                      >
                        <div class="explore-chip__inner">
                          <div class="explore-chip__icon-circle">
                            <img v-if="chip.icon" :src="chip.icon" :alt="chip.name" />
                            <i v-else class="mdi mdi-robot"></i>
                          </div>
                          <span class="explore-chip__label" :title="chip.name">{{ chip.name }}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        class="ranking-slot__clear"
                        :data-testid="`annotate-rank-clear-${chip.id}`"
                        @click="clearRankIndex(index)"
                      >
                        Clear
                      </button>
                    </div>
                    
                  </div>
                </div>
                <div
                  class="ranking-unranked"
                  data-testid="annotate-rank-unranked"
                  @dragover.prevent
                  @drop="handleDropUnranked"
                >
                  <div class="ranking-unranked__label">Unranked</div>
                  <div class="ranking-unranked__list">
                    <div
                      v-for="chip in unrankedModelChips"
                      :key="chip.id"
                      class="explore-chip explore-chip--lg ranking-chip"
                      draggable="true"
                      :data-testid="`annotate-rank-unranked-${chip.id}`"
                      @dragstart="handleDragStart(chip.id)"
                    >
                      <div class="explore-chip__inner">
                        <div class="explore-chip__icon-circle">
                          <img v-if="chip.icon" :src="chip.icon" :alt="chip.name" />
                          <i v-else class="mdi mdi-robot"></i>
                        </div>
                        <span class="explore-chip__label" :title="chip.name">{{ chip.name }}</span>
                      </div>
                    </div>
                    <div v-if="unrankedModelChips.length === 0" class="ranking-unranked__empty">
                      All responses ranked.
                    </div>
                  </div>
                </div>
              </div>
            </div>

              <div class="annotate-chip-card mt-4" data-testid="annotate-assign-tags">
                <div class="annotate-chip-card__title">Select Tags</div>
                <div class="explore-chip-row">
                  <button
                    v-for="tag in displayTags"
                    :key="tag.id"
                    type="button"
                    :class="[
                      'explore-chip',
                      'explore-chip--lg',
                      selectedTags.includes(Number(tag.id)) ? 'explore-chip--active' : ''
                    ]"
                    :style="{
                      backgroundColor: selectedTags.includes(Number(tag.id)) ? `${tag.display_color}25` : '',
                      borderColor: selectedTags.includes(Number(tag.id)) ? `${tag.display_color}50` : ''
                    }"
                    :data-testid="`annotate-tag-${tag.id}`"
                    @click="toggleTag(tag.id)"
                  >
                    <div class="explore-chip__inner">
                      <i
                        :class="[tag.display_icon ? tag.display_icon : 'uil uil-cube', 'explore-chip__icon']"
                        :style="{ color: tag.display_color }"
                      ></i>
                      <span class="explore-chip__label" :title="tag.name">{{ tag.name }}</span>
                    </div>
                  </button>
                </div>
              </div>

            </div>
            <div v-else-if="detailsTab === 'prompt'" class="annotate-section">
              <div class="annotate-card-title">Task Prompt</div>
              <MarkdownRenderer
                :model-value="normalizeModelText(selectedTask?.task_prompt || '—')"
                :content-only="true"
              />
              <div v-if="referenceFiles.length > 0" class="reference-files">
                <div class="annotate-card-title">Reference Files</div>
                <div class="reference-files__list">
                  <div v-for="(ref, index) in referenceFiles" :key="index" class="reference-file">
                    <template v-if="isImageFile(getReferenceFileName(ref)) && getReferenceFileUrl(ref)">
                      <div class="reference-file__image">
                        <img :src="getReferenceFileUrl(ref)" :alt="getReferenceFileName(ref)" />
                      </div>
                      <div class="reference-file__meta">
                        <span class="reference-file__name">{{ getReferenceFileName(ref) }}</span>
                      </div>
                    </template>
                      <template v-else>
                      <div class="reference-file__meta">
                        <span class="reference-file__name">{{ getReferenceFileName(ref) || 'Reference file' }}</span>
                        <span class="reference-file__type">
                          {{ (getReferenceFileName(ref).split('.').pop() || 'file').toUpperCase() }}
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="detailsTab === 'environment'" class="annotate-section">
              <div v-if="isLoading.environment" class="text-white/40 text-sm py-4">Loading environment...</div>
              <div v-else-if="!harborEnvironment.task_toml && !harborEnvironment.dockerfile" class="text-white/40 text-sm py-4">
                No harbor environment files found for this task.
              </div>
              <template v-else>
                <div v-if="harborEnvironment.task_toml" class="mb-4">
                  <div class="annotate-card-title">task.toml</div>
                  <pre class="harbor-env-code">{{ harborEnvironment.task_toml }}</pre>
                </div>
                <div v-if="harborEnvironment.dockerfile">
                  <div class="annotate-card-title">Dockerfile</div>
                  <pre class="harbor-env-code">{{ harborEnvironment.dockerfile }}</pre>
                </div>
              </template>
            </div>
            <div v-else-if="detailsTab === 'answer'" class="annotate-section">
              <div class="annotate-card-title">Golden Answer</div>
              <MarkdownRenderer :model-value="normalizeModelText(goldenAnswer)" :content-only="true" />
            </div>
            <div v-else class="annotate-section">
              <div class="annotate-card-title">Grading Criteria</div>
              <div class="criteria-list">
                  <div v-for="(criterion, index) in taskRubric" :key="getCriterionKey(criterion)" class="criterion-item">
                  <div class="criterion-title">{{ index + 1 }}. {{ criterion.name }}</div>
                  <div class="criterion-meta">
                    <span
                      v-if="getCriterionVerdict(criterion)"
                      :class="[
                        'criterion-chip',
                        getCriterionVerdict(criterion) === 'pass' ? 'criterion-chip--pass' : '',
                        getCriterionVerdict(criterion) === 'fail' ? 'criterion-chip--fail' : '',
                        getCriterionVerdict(criterion) === 'flagged' ? 'criterion-chip--flagged' : ''
                      ]"
                    >
                      {{ getCriterionVerdict(criterion) }}
                    </span>
                    <span
                      v-if="criterion.weight != null"
                      class="criterion-chip criterion-chip--weight"
                    >
                      {{ Number(criterion.weight) }}%
                    </span>
                  </div>
                  <div
                    v-if="getCriterionJudgeEntries(criterion).length"
                    class="criterion-judge"
                  >
                    <div class="criterion-judge__toggle-row">
                      <button
                        type="button"
                        class="criterion-judge__toggle"
                        :data-testid="`annotate-judge-toggle-${formatCriterionTestId(getCriterionKey(criterion))}`"
                        @click="toggleJudgeExpanded(criterion)"
                      >
                        <span>LLM Jury</span>
                        <i class="uil" :class="isJudgeExpanded(criterion) ? 'uil-angle-down' : 'uil-angle-right'"></i>
                      </button>
                      <v-tooltip location="top" max-width="280">
                        <template v-slot:activator="{ props: tooltipProps }">
                          <span class="criterion-judge__tooltip" v-bind="tooltipProps">i</span>
                        </template>
                        <span>View verdicts and justifications from each LLM judge.</span>
                      </v-tooltip>
                    </div>
                    <div
                      v-if="isJudgeExpanded(criterion)"
                      class="criterion-judge__panel"
                      :data-testid="`annotate-judge-panel-${formatCriterionTestId(getCriterionKey(criterion))}`"
                    >
                      <div
                        v-for="(judge, judgeIndex) in getCriterionJudgeEntries(criterion)"
                        :key="judge.model"
                        class="criterion-judge__card"
                      >
                        <div class="criterion-judge__header">
                          <span class="criterion-judge__model">Judge-{{ judgeIndex + 1 }} ({{ judge.model }})</span>
                          <span class="criterion-judge__votes">
                            votes {{ judge.passCount }}/{{ judge.totalVotes || 0 }}
                          </span>
                          <span
                            :class="[
                              'criterion-judge__verdict',
                              judge.verdict === 'pass' ? 'criterion-judge__verdict--pass' : '',
                              judge.verdict === 'fail' ? 'criterion-judge__verdict--fail' : '',
                              judge.verdict === 'flagged' ? 'criterion-judge__verdict--flagged' : ''
                            ]"
                          >
                            {{ judge.verdict }}
                          </span>
                        </div>
                        <div class="criterion-judge__justifications">
                          <div
                            v-for="(justification, jIndex) in judge.justifications"
                            :key="`${judge.model}-${jIndex}`"
                            class="criterion-judge__justification"
                          >
                            {{ justification }}
                          </div>
                          <div v-if="judge.justifications.length === 0" class="criterion-judge__justification">
                            No justification provided.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="criterion-desc">
                    <MarkdownRenderer
                      :model-value="criterion.semanticPrompt || criterion.description || '—'"
                      :content-only="true"
                    />
                  </div>
                </div>
                <div v-if="taskRubric.length === 0" class="text-white/60 text-sm">No criteria available</div>
              </div>
            </div>


          </div>
        </div>
      </div>

    </div>
  </div>
  </div>
  </div>
</template>

<style scoped lang="postcss">
.back-button {
  @apply flex items-center gap-1 px-4 py-1.5 rounded-full text-sm bg-echoes-background-gray text-white/80 hover:text-white transition-colors;
}

.explore-chip-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.explore-chip {
  border-radius: 9999px;
  padding: 0 12px;
  height: 32px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(4px);
  color: rgba(255, 255, 255, 0.8);
  transition: color 200ms ease, background-color 200ms ease, border-color 200ms ease;
  width: fit-content;
}

.explore-chip:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.explore-chip--active {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #fe3e01;
}

.explore-chip--solid {
  background: rgba(255, 255, 255, 0.1);
}

.explore-chip__inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.explore-chip__label {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.explore-chip__icon-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.explore-chip__icon-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.explore-chip__icon-circle i {
  font-size: 10px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.8);
}

.explore-chip__icon {
  font-size: 16px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.8);
}

.explore-chip--lg {
  height: 40px;
  padding: 0 16px;
  font-size: 13px;
}

.annotate-chip-card {
  @apply bg-echoes-background-black rounded-xl border border-echoes-border-light p-3 flex flex-col gap-2;
}

.annotate-chip-card__title {
  @apply text-white/80 text-sm font-semibold;
}

.annotate-model-card {
  padding: 12px;
  flex-shrink: 0;
}

.annotate-model-row {
  @apply flex items-center flex-wrap gap-3;
}

.explore-chip-row--compact {
  @apply flex flex-wrap gap-2;
}

.annotate-model-card .explore-chip--lg {
  padding: 4px 10px;
}

.annotate-task-view {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.annotate-task-body {
  @apply flex flex-col gap-2;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.annotate-grid {
  @apply grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.annotate-task-header {
  @apply flex items-center justify-between gap-3 flex-shrink-0;
}

.ranking-grid {
  @apply flex flex-col gap-4;
}

.ranking-empty {
  @apply text-xs text-white/50;
}

.ranking-slots {
  @apply grid gap-3;
}

.ranking-slot {
  @apply border border-echoes-border-light/40 rounded-lg p-3 bg-echoes-background-gray/40;
}

.ranking-slot__label {
  @apply text-xs uppercase text-white/60 mb-2 flex items-center justify-between gap-2;
}

.ranking-slot__hint {
  @apply text-[11px] normal-case text-white/40;
}

.ranking-slot__chip {
  @apply flex items-center justify-between gap-3;
}

.ranking-slot__empty {
  @apply text-xs text-white/50 italic;
}

.ranking-slot__clear {
  @apply text-xs text-white/60 hover:text-white;
}

.ranking-unranked {
  @apply border border-echoes-border-light/30 rounded-lg p-3 bg-echoes-background-black/40;
}

.ranking-unranked__label {
  @apply text-xs uppercase text-white/60 mb-2;
}

.ranking-unranked__list {
  @apply flex flex-wrap gap-2;
}

.ranking-unranked__empty {
  @apply text-xs text-white/50 italic;
}

.ranking-chip {
  @apply cursor-move;
}

.passfail-grid {
  @apply bg-echoes-background-black rounded-xl border border-echoes-border-light p-4;
}

.passfail-grid__table {
  @apply w-full overflow-x-auto;
}

.passfail-grid__table table {
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
}

.passfail-grid__cell {
  @apply px-3 py-3 text-xs text-white/80 border-b border-echoes-border-light;
}

.passfail-grid__expanded {
  @apply bg-echoes-background-black/40 border-b border-echoes-border-light;
}

.passfail-grid__expand-btn {
  @apply w-7 h-7 rounded-full bg-echoes-background-gray text-white/80 flex items-center justify-center transition-colors;
}

.passfail-grid__expand-btn:hover {
  @apply text-white bg-echoes-background-gray/70;
}

.criterion-grid {
  display: grid;
  grid-template-columns: 1fr repeat(var(--criterion-columns, 1), minmax(120px, 1fr));
  gap: 8px;
  width: 100%;
}

.criterion-grid__header {
  @apply text-[11px] uppercase text-white/60 font-semibold;
}

.criterion-grid__header--center {
  @apply text-center;
}

.criterion-grid__cell {
  @apply bg-echoes-background-gray rounded-lg p-1.5 text-white/70 text-xs;
}

.criterion-grid__cell--center {
  @apply flex items-center justify-center;
}

.criterion-grid__title-row {
  @apply flex items-center gap-2;
}

.criterion-grid__title {
  @apply text-white/90 font-semibold text-xs truncate;
  max-width: 240px;
}

.passfail-grid__cell--left {
  @apply text-left;
}

.passfail-grid__cell--center {
  @apply text-center;
}

.passfail-grid__cell--clickable {
  @apply cursor-pointer;
}

.passfail-chip {
  @apply inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold border;
}

.passfail-chip--pass {
  @apply border-green-500 text-green-300 bg-green-500/20;
}

.passfail-chip--fail {
  @apply border-red-500 text-red-300 bg-red-500/20;
}

.passfail-chip--empty {
  @apply border-echoes-border-light text-white/50 bg-echoes-background-gray;
}

.passfail-chip--model {
  height: 28px;
  padding: 0 10px;
  font-size: 11px;
}

.passfail-chip--model .explore-chip__label {
  font-size: 11px;
}



.annotate-response,
.annotate-details {
  @apply bg-echoes-background-black rounded-xl border border-echoes-border-light p-3 flex flex-col gap-2;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  flex: 1;
}

.annotate-response-body {
  @apply text-white/80 text-sm overflow-y-auto bg-echoes-background-gray rounded-lg p-2;
  flex: 1;
  min-height: 0;
}

.annotate-thinking {
  @apply border-t border-echoes-border-light pt-3;
}

.annotate-tabs {
  @apply flex items-center gap-2;
}

.annotate-tab {
  @apply px-3 py-1 rounded-full bg-echoes-background-gray text-white/70 text-xs;
}

.annotate-tab--active {
  @apply border border-echoes-primary text-white bg-echoes-background-black;
}

.annotate-detail-body {
  @apply flex flex-col gap-3 overflow-y-auto;
  flex: 1;
  min-height: 0;
}

.annotate-section {
  @apply flex flex-col gap-2;
}

.annotate-response__header {
  @apply flex items-center justify-between gap-3 flex-shrink-0;
}

.annotate-version-select {
  @apply min-w-[140px];
}

.annotate-card-title {
  @apply text-white/80 text-sm font-semibold;
}

.harbor-env-code {
  @apply whitespace-pre-wrap break-words text-xs text-white/70 leading-relaxed m-0 mt-2 p-3 rounded-lg bg-black/30 border border-white/10 overflow-x-auto;
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace, ui-monospace;
}

.reference-files {
  @apply flex flex-col gap-2 mt-3;
}

.reference-files__list {
  @apply flex flex-col gap-3;
}

.reference-file {
  @apply bg-echoes-background-gray rounded-lg p-3 border border-echoes-border-light/40;
}

.reference-file__image {
  @apply w-full h-64 bg-black/30 rounded-md overflow-hidden border border-echoes-border-light/30;
}

.reference-file__image img {
  @apply w-full h-full object-contain;
}

.reference-file__meta {
  @apply flex items-center justify-between text-xs text-white/70 mt-2;
}

.reference-file__name {
  @apply text-white/90 font-medium truncate;
  max-width: 75%;
}

.reference-file__type {
  @apply text-white/50 uppercase;
}


.criteria-list {
  @apply flex flex-col gap-2;
}

.criterion-item {
  @apply bg-echoes-background-gray rounded-lg p-2 text-white/70 text-xs;
}

.criterion-title {
  @apply text-white/90 font-semibold;
}

.criterion-meta {
  @apply flex flex-wrap items-center gap-2 mt-1;
}

.criterion-judge {
  @apply mt-2;
}

.criterion-judge__toggle-row {
  @apply flex items-center gap-2;
}

.criterion-judge__toggle {
  @apply text-xs text-white/70 hover:text-white flex items-center gap-1;
}

.criterion-judge__tooltip {
  @apply inline-flex items-center justify-center w-4 h-4 rounded-full border border-echoes-border-light text-[10px] text-white/60 bg-echoes-background-gray;
}

.criterion-judge__panel {
  @apply mt-2 flex flex-col gap-2;
}

.criterion-judge__card {
  @apply bg-echoes-background-black/40 border border-echoes-border-light/30 rounded-md p-2;
}

.criterion-judge__header {
  @apply flex flex-wrap items-center gap-2 text-xs text-white/80;
}

.criterion-judge__model {
  @apply font-semibold text-white/90;
}

.criterion-judge__votes {
  @apply text-[10px] uppercase text-white/50;
}

.criterion-judge__verdict {
  @apply text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 border;
}

.criterion-judge__verdict--pass {
  @apply border-green-500 text-green-300 bg-green-500/20;
}

.criterion-judge__verdict--fail {
  @apply border-red-500 text-red-300 bg-red-500/20;
}

.criterion-judge__verdict--flagged {
  @apply border-yellow-500 text-yellow-300 bg-yellow-500/20;
}

.criterion-judge__justifications {
  @apply mt-2 flex flex-col gap-1 text-xs text-white/70;
}

.criterion-judge__justification {
  @apply bg-echoes-background-gray/60 rounded-md p-2;
}

.criterion-chip {
  @apply inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-semibold border;
  text-transform: uppercase;
}

.criterion-chip--pass {
  @apply border-green-500 text-green-300 bg-green-500/20;
}

.criterion-chip--fail {
  @apply border-red-500 text-red-300 bg-red-500/20;
}

.criterion-chip--flagged {
  @apply border-yellow-500 text-yellow-300 bg-yellow-500/20;
}

.criterion-chip--weight {
  @apply border-echoes-border-light text-white/70 bg-echoes-background-black/40;
  text-transform: none;
}

.annotation-panel {
  @apply bg-echoes-background-black rounded-xl border border-echoes-border-light p-4 flex flex-col gap-4;
}

.annotation-tags {
  @apply flex flex-wrap items-center gap-2;
}

.tag-chip {
  @apply px-3 py-1 rounded-full bg-echoes-background-gray text-white/70 text-xs;
}

.tag-chip--active {
  @apply border border-echoes-primary text-white;
}

.notes-container {
  @apply flex flex-col gap-3;
}

.notes-header {
  @apply text-white/80 text-sm font-semibold flex items-center gap-2;
}


.notes-body {
  @apply flex flex-col gap-3;
}

.notes-placeholder {
  @apply bg-echoes-background-gray/60 text-white/50 text-sm rounded-lg px-4 py-3;
}

.note-row {
  @apply flex items-start gap-3;
}

.note-index {
  @apply w-7 h-7 rounded-full bg-echoes-background-gray text-white text-xs flex items-center justify-center;
}

.note-content {
  @apply flex-1 flex flex-col gap-2;
}

.note-selection {
  @apply text-xs text-white/60;
}

.note-textarea {
  @apply w-full rounded-lg bg-echoes-background-gray text-white/80 text-sm p-2 border border-echoes-border-light/50 focus:outline-none focus:ring-2 focus:ring-echoes-primary;
  min-height: 80px;
}

.note-add {
  @apply w-9 h-9 rounded-full bg-echoes-background-gray text-white flex items-center justify-center text-lg;
}

.note-remove {
  @apply text-xs text-white/50 hover:text-white;
}

.notes-footer {
  @apply flex justify-end;
}

.save-button {
  @apply px-4 py-2 rounded-lg bg-echoes-primary text-white text-sm font-semibold;
}
</style>
